import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PortfolioWizard from '../components/PortfolioWizard';
// Removed Apr 21 2026 per FA-11 scrub: Enhanced BI + Real-Time Risk tabs
// overlap Compliance Posture and contradict the weekly cadence. Financial
// Impact retained — it serves FA-11-c (insurance premium) and FA-11-d
// (ATO-markup sizing).
import FinancialImpactAnalysis from '../../../components/portfolio/FinancialImpactAnalysis';
import CompanyDetailModal from '../../../components/portfolio/CompanyDetailModal';
import RevenueCalculationModal from '../../../components/portfolio/RevenueCalculationModal';
import { measuredRiskScore } from '../../../components/portfolio/measuredRiskScore';
import { clientRiskBadge, comparePortfolioClients } from '../../../components/portfolio/portfolioClientStatus';
import BandDistribution from '../../../components/portfolio/BandDistribution';
import { getRiskBandStyle, legacyCrsBandToKey, getRiskBand } from '../../../utils/bands';
import * as Dialog from '@radix-ui/react-dialog';
import apiConfig from '../../../config/apiConfig';

const viewParamMap = {
  compliance: 'compliance',
  financial: 'financial',
  companies: 'companies',
};

// Re-ask for the heatmap while industries are still building server-side: every 15 s, at most 8 times.
const HEATMAP_REFRESH_MS = 15 * 1000;
const HEATMAP_REFRESH_ATTEMPTS = 8;

const PortfolioDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialView = viewParamMap[searchParams.get('view')] || 'compliance';
  const [dashboardData, setDashboardData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPortfolioWizard, setShowPortfolioWizard] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [showPortfolioDetails, setShowPortfolioDetails] = useState(false);
  const [analyticsView, setAnalyticsView] = useState(initialView);
  const [showCompanyDetailModal, setShowCompanyDetailModal] = useState(false);
  const [showRevenueCalculationModal, setShowRevenueCalculationModal] = useState(false);
  const [selectedCompanyForModal, setSelectedCompanyForModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'risk', 'revenue', 'status'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [crsBatchData, setCrsBatchData] = useState({}); // CRS data keyed by companyId

  // Tile 15 — PM → Coordinator request dialog state
  const [requestDialogCompany, setRequestDialogCompany] = useState(null);
  const [requestForm, setRequestForm] = useState({
    request_type: 'update_assessment',
    priority: 'normal',
    request_message: '',
    due_date: ''
  });
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(null);

  const openRequestDialog = (company) => {
    setRequestDialogCompany(company);
    setRequestForm({ request_type: 'update_assessment', priority: 'normal', request_message: '', due_date: '' });
    setRequestError(null);
    setRequestSuccess(null);
  };

  const submitRequest = async () => {
    if (!requestDialogCompany || !requestForm.request_message.trim()) {
      setRequestError('Please enter a message describing what you need.');
      return;
    }
    setRequestSubmitting(true);
    setRequestError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const payload = {
        client_company_id: requestDialogCompany.company_id,
        request_type: requestForm.request_type,
        request_message: requestForm.request_message.trim(),
        priority: requestForm.priority,
        due_date: requestForm.due_date || null
      };
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/requests`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setRequestSuccess(`Request #${data.request.id} sent to ${requestDialogCompany.company_name}.`);
      setTimeout(() => setRequestDialogCompany(null), 1500);
    } catch (err) {
      setRequestError(err.message || 'Failed to submit request');
    } finally {
      setRequestSubmitting(false);
    }
  };

  // Sync tab when nav links change the ?view param
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam && viewParamMap[viewParam]) {
      setAnalyticsView(viewParamMap[viewParam]);
    } else if (!viewParam) {
      setAnalyticsView('compliance');
    }
  }, [searchParams]);

  // Tile 15 — outstanding PM requests (for the Sprint 3 Action Queue)
  const [outstandingRequests, setOutstandingRequests] = useState([]);
  const [actionQueueCollapsed, setActionQueueCollapsed] = useState(false);

  // Tile 12 — Outside Pressures portfolio heatmap (loaded lazily on tab open)
  const [outsidePressuresHeatmap, setOutsidePressuresHeatmap] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const heatmapRefreshTimer = useRef(null);

  // FA-11-e — Enterprise/BISO view mode (enterprise CISO over BISOs). Relabels
  // Client→Branch throughout, hides allocation actions the archetype doesn't
  // perform, adds a header mode badge. Persists in localStorage so the toggle
  // survives reloads.
  const [enterpriseMode, setEnterpriseMode] = useState(() => {
    try { return localStorage.getItem('foundations-enterprise-mode') === 'true'; } catch { return false; }
  });
  const toggleEnterpriseMode = () => {
    setEnterpriseMode((prev) => {
      const next = !prev;
      try { localStorage.setItem('foundations-enterprise-mode', String(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const roleLabels = enterpriseMode
    ? { clientSingular: 'Branch', clientPlural: 'Branches', dashboardTitle: 'Enterprise Risk Oversight', subtitle: 'Branch CISO strategic snapshot' }
    : { clientSingular: 'Client', clientPlural: 'Clients', dashboardTitle: 'Portfolio Management Dashboard', subtitle: null };

  // FA-11-f — Board Report modal (aggregate + trend, board-ready export)
  const [boardReportOpen, setBoardReportOpen] = useState(false);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const openBoardReport = async () => {
    setBoardReportOpen(true);
    if (trendData || trendLoading) return;
    setTrendLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/trend`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data?.success) setTrendData(data);
    } catch {
      // Non-fatal
    } finally {
      setTrendLoading(false);
    }
  };

  // FA-11-b — Onboarding Placement wizard (Sprint 4)
  const [placementClient, setPlacementClient] = useState(null);
  const [placementRecommendation, setPlacementRecommendation] = useState(null);
  const [placementLoading, setPlacementLoading] = useState(false);
  const [placementSubmittingId, setPlacementSubmittingId] = useState(null);
  const [placementError, setPlacementError] = useState(null);
  const [placementSuccess, setPlacementSuccess] = useState(null);

  const openPlacementWizard = async (company) => {
    setPlacementClient(company);
    setPlacementRecommendation(null);
    setPlacementError(null);
    setPlacementSuccess(null);
    setPlacementLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/placement-recommendation/${company.company_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setPlacementRecommendation(data);
    } catch (err) {
      setPlacementError(err.message || 'Failed to load placement recommendation');
    } finally {
      setPlacementLoading(false);
    }
  };

  const confirmPlacement = async (groupId) => {
    if (!placementClient) return;
    setPlacementSubmittingId(groupId);
    setPlacementError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${groupId}/members`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: placementClient.company_id })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setPlacementSuccess(`Placed ${placementClient.company_name} into the portfolio.`);
      // Refresh portfolios list so counts update
      fetchPortfolios();
      setTimeout(() => {
        setPlacementClient(null);
        setPlacementSuccess(null);
      }, 1400);
    } catch (err) {
      setPlacementError(err.message || 'Failed to place client');
    } finally {
      setPlacementSubmittingId(null);
    }
  };

  // Fetch portfolio data on component mount
  useEffect(() => {
    fetchPortfolioData();
    fetchPortfolios();
    fetchOutstandingRequests();
  }, []);

  // Fetch batch CRS data when companies are loaded
  useEffect(() => {
    if (companies.length > 0) {
      fetchBatchCRSData();
    }
  }, [companies]);

  // The server answers within ~20 s and marks industries it has not built yet `pending`; it keeps
  // building them, so ask again until none are pending (bounded, and stopped on unmount).
  const fetchOutsidePressuresHeatmap = async (attempt = 0) => {
    if (attempt === 0 && (outsidePressuresHeatmap || heatmapLoading)) return;
    if (attempt === 0) setHeatmapLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      if (!token) return;
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/outside-pressures-heatmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.success) {
        setOutsidePressuresHeatmap(data);
        if (data.pending_count > 0 && attempt < HEATMAP_REFRESH_ATTEMPTS) {
          heatmapRefreshTimer.current = setTimeout(() => fetchOutsidePressuresHeatmap(attempt + 1), HEATMAP_REFRESH_MS);
        }
      }
    } catch {
      // Non-fatal
    } finally {
      if (attempt === 0) setHeatmapLoading(false);
    }
  };

  useEffect(() => () => clearTimeout(heatmapRefreshTimer.current), []);

  // Lazy-load the heatmap only when the Compliance Posture tab is viewed
  useEffect(() => {
    if (analyticsView === 'compliance') fetchOutsidePressuresHeatmap();
  }, [analyticsView]);

  const fetchOutstandingRequests = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      if (!token) return;
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.success && Array.isArray(data.requests)) {
        // Outstanding = anything not terminal (not completed, not declined)
        setOutstandingRequests(data.requests.filter(r => !['completed', 'declined'].includes(r.status)));
      }
    } catch {
      // Non-fatal
    }
  };

  const fetchBatchCRSData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      if (!token) return;

      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/crs-batch`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.results) {
          // Convert array to object keyed by companyId
          const crsMap = {};
          result.results.forEach(item => {
            crsMap[item.companyId] = item.crs;
          });
          setCrsBatchData(crsMap);
        }
      }
    } catch (err) {
      console.error('❌ Error fetching batch CRS data:', err);
    }
  };

  const fetchPortfolios = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      if (!token) return;

      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPortfolios(result.portfolioGroups || []);
        }
      }
    } catch (err) {
      console.error('❌ Error fetching portfolios:', err);
    }
  };

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Fetch dashboard summary
      const dashboardResponse = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!dashboardResponse.ok) {
        throw new Error(`Dashboard fetch failed: ${dashboardResponse.status}`);
      }

      const dashboardResult = await dashboardResponse.json();
      
      if (dashboardResult.success) {
        setDashboardData(dashboardResult.dashboard);
        
        // Fetch detailed companies list
        const companiesResponse = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/companies`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (companiesResponse.ok) {
          const companiesResult = await companiesResponse.json();
          if (companiesResult.success) {
            setCompanies(companiesResult.companies);
          }
        }
      } else {
        throw new Error(dashboardResult.error || 'Failed to fetch dashboard data');
      }

    } catch (err) {
      console.error('❌ Portfolio dashboard error:', err);
      setError(err.message);
      
      // If authentication error, redirect to login
      if (err.message.includes('Authentication') || err.message.includes('401') || err.message.includes('403')) {
        localStorage.removeItem('token');
        localStorage.removeItem('foundations-token');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount) || amount === 0) return 'N/A';

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numAmount) || !isFinite(numAmount)) return 'N/A';
    
    if (numAmount >= 1000000000) {
      return `$${(numAmount / 1000000000).toFixed(2)}B`;
    } else if (numAmount >= 1000000) {
      return `$${(numAmount / 1000000).toFixed(2)}M`;
    } else if (numAmount >= 1000) {
      return `$${(numAmount / 1000).toFixed(0)}K`;
    } else {
      return `$${numAmount.toFixed(0)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Enhanced portfolio calculations
  const calculateEnhancedStats = (companies) => {
    if (!companies || companies.length === 0) {
      return {
        totalCompanies: 0,
        companiesWithAssessments: 0,
        totalRevenueAtRisk: 0,
        averageRiskScore: 0,
        portfolioHealth: 'Unknown',
        criticalRiskCompanies: 0,
        highRiskCompanies: 0,
        mediumRiskCompanies: 0,
        lowRiskCompanies: 0,
        totalAnnualRevenue: 0,
        averageRecoveryDays: 0
      };
    }

    let totalRevenueAtRisk = 0;
    let totalAnnualRevenue = 0;
    let totalRiskScore = 0; // Initialize as number
    let companiesWithRiskScore = 0;
    let criticalRiskCompanies = 0;
    let highRiskCompanies = 0;
    let mediumRiskCompanies = 0;
    let lowRiskCompanies = 0;

    companies.forEach(company => {
      // Check if company has a completed assessment
      // The backend now provides risk_score directly from assessment_sessions table
      const hasCompletedAssessment = company.completion_percentage === 100 || 
                                   company.completion_percentage === "100" ||
                                   (company.risk_score && parseFloat(company.risk_score) > 0);

      // Use the risk_score directly from backend (already from assessment_sessions)
      // Parse as number in case it comes as string from backend
      const riskScore = parseFloat(company.risk_score) || 0;
      
      if (hasCompletedAssessment && riskScore > 0 && !isNaN(riskScore)) {
        // Ensure both values are numbers before addition
        const currentTotal = parseFloat(totalRiskScore) || 0;
        const currentRisk = parseFloat(riskScore) || 0;
        totalRiskScore = currentTotal + currentRisk;
        companiesWithRiskScore++;
        
        if (riskScore >= 70) criticalRiskCompanies++;
        else if (riskScore >= 50) highRiskCompanies++;
        else if (riskScore >= 30) mediumRiskCompanies++;
        else lowRiskCompanies++;
      }

      // Calculate revenue data - use annual_revenue from backend (parse as number since it comes as string)
      const annualRevenue = parseFloat(company.annual_revenue) || 0;
      
      if (annualRevenue > 0 && !isNaN(annualRevenue)) {
        const dailyRevenue = annualRevenue / 365;
        
        // Use actual risk score if available, otherwise estimate based on industry average (45)
        const riskForCalculation = (hasCompletedAssessment && riskScore > 0) ? riskScore : 45;
        const recoveryDays = Math.max(1, Math.round(riskForCalculation / 8));
        const revenueAtRisk = dailyRevenue * recoveryDays;

        totalAnnualRevenue += annualRevenue;
        if (!isNaN(revenueAtRisk)) {
          totalRevenueAtRisk += revenueAtRisk;
        }
      }
    });

    // Ensure we have a valid number for average risk score
    const rawAverage = companiesWithRiskScore > 0 
      ? (totalRiskScore / companiesWithRiskScore) 
      : 0;
    
    const averageRiskScore = !isNaN(rawAverage) ? Math.round(rawAverage) : 0;

    // Determine portfolio health
    let portfolioHealth = 'Unknown';
    if (averageRiskScore > 0) {
      if (averageRiskScore < 30) portfolioHealth = 'Excellent';
      else if (averageRiskScore < 50) portfolioHealth = 'Good';
      else if (averageRiskScore < 70) portfolioHealth = 'Needs Attention';
      else portfolioHealth = 'Critical';
    }

    return {
      totalCompanies: companies.length,
      companiesWithAssessments: companiesWithRiskScore,
      totalRevenueAtRisk,
      averageRiskScore,
      portfolioHealth,
      criticalRiskCompanies,
      highRiskCompanies,
      mediumRiskCompanies,
      lowRiskCompanies,
      totalAnnualRevenue,
      averageRecoveryDays: Math.round(averageRiskScore / 8) || 0
    };
  };

  const handleViewCompanyDetails = (company) => {
    setSelectedCompanyForModal(company);
    setShowCompanyDetailModal(true);
  };

  // Portfolio creation handlers
  const handleCreatePortfolio = () => {
    setShowPortfolioWizard(true);
  };

  const handleViewPortfolioDetails = async (portfolio) => {
    setSelectedPortfolio(portfolio);
    setShowPortfolioDetails(true);
    
    // Fetch detailed portfolio information including companies
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${portfolio.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Merge portfolio data with companies from the response
          setSelectedPortfolio({
            ...portfolio, 
            ...result.portfolio,
            companies: result.portfolio?.companies || []
          });
        }
      }
    } catch (err) {
      console.error('Error fetching portfolio details:', err);
    }
  };

  const handleClosePortfolioWizard = () => {
    setShowPortfolioWizard(false);
  };

  const [portfolioToDelete, setPortfolioToDelete] = useState(null);
  const [portfolioDeleting, setPortfolioDeleting] = useState(false);

  // Portfolio group Edit dialog — rename + member swap
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', maxCompanies: '', memberIds: [] });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const openEditPortfolio = async (portfolio) => {
    setEditError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${portfolio.id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const memberIds = (data?.portfolio?.companies || []).map((c) => c.id || c.company_id).filter(Boolean);
      setEditingPortfolio(portfolio);
      setEditForm({
        name: portfolio.name || '',
        description: portfolio.description || '',
        maxCompanies: portfolio.max_companies != null ? String(portfolio.max_companies) : '',
        memberIds
      });
    } catch (err) {
      setEditError(err.message || 'Failed to load portfolio details');
      setEditingPortfolio(portfolio);
      setEditForm({
        name: portfolio.name || '',
        description: portfolio.description || '',
        maxCompanies: portfolio.max_companies != null ? String(portfolio.max_companies) : '',
        memberIds: []
      });
    }
  };

  const saveEditPortfolio = async () => {
    if (!editingPortfolio) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const body = {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        companyIds: editForm.memberIds
      };
      if (editForm.maxCompanies !== '' && !Number.isNaN(Number(editForm.maxCompanies))) {
        body.maxCompanies = Number(editForm.maxCompanies);
      }
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${editingPortfolio.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      fetchPortfolios();
      setEditingPortfolio(null);
    } catch (err) {
      setEditError(err.message || 'Failed to save portfolio');
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDeletePortfolio = async () => {
    if (!portfolioToDelete) return;
    setPortfolioDeleting(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      const res = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${portfolioToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPortfolios((prev) => prev.filter((p) => p.id !== portfolioToDelete.id));
      setPortfolioToDelete(null);
    } catch (err) {
      console.error('Failed to delete portfolio:', err);
    } finally {
      setPortfolioDeleting(false);
    }
  };

  // Insights-style modal handlers
  const handleCompanyClick = (company) => {
    setSelectedCompanyForModal(company);
    setShowCompanyDetailModal(true);
  };

  const handleRevenueCalculationClick = (company) => {
    setSelectedCompanyForModal(company);
    setShowRevenueCalculationModal(true);
  };

  const handleModalAction = (action, company) => {
    switch (action) {
      case 'view':
        navigate(`/assessment/${company.company_id}/results`);
        break;
      case 'calculation':
        handleRevenueCalculationClick(company);
        break;
      case 'new':
        break;
      case 'export':
        break;
      default:
        break;
    }
  };

  // Filter and sort companies
  const getFilteredAndSortedCompanies = () => {
    let filtered = companies.filter(company => {
      const searchLower = searchTerm.toLowerCase();
      return (
        company.company_name?.toLowerCase().includes(searchLower) ||
        company.latest_assessment?.industry_vertical?.toLowerCase().includes(searchLower) ||
        company.access_level?.toLowerCase().includes(searchLower)
      );
    });

    // Unfinished clients and unanswered revenue sort after every real value, by progress — never
    // as 0 (portfolioClientStatus.ts).
    filtered.sort((a, b) => comparePortfolioClients(a, b, sortBy, sortOrder));

    return filtered;
  };

  const handlePortfolioCreated = async (portfolioData) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Create the portfolio via API
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(portfolioData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Portfolio creation failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Close the wizard first to prevent UI blocking
        setShowPortfolioWizard(false);
        
        // Show success message
        alert(`Portfolio "${portfolioData.name}" created successfully!`);
        
        // Refresh the dashboard data in the background
        fetchPortfolioData().catch(err => {
          console.error('❌ Error refreshing dashboard after portfolio creation:', err);
        });
        fetchPortfolios().catch(err => {
          console.error('❌ Error refreshing portfolios after creation:', err);
        });
      } else {
        throw new Error(result.error || 'Failed to create portfolio');
      }

    } catch (error) {
      console.error('❌ Error creating portfolio:', error);
      // Re-throw the error so the wizard can handle it
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a192f] flex items-center justify-center">
        <div className="ra-glass rounded-lg shadow-[0_20px_25px_rgba(0,0,0,0.4)] p-8 max-w-md w-full mx-4 text-center">
          <div className="animate-spin w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-[#e6f1ff] mb-2">Loading Portfolio Dashboard</h2>
          <p className="text-[#a8b2d1]">Fetching your client portfolio data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a192f] flex items-center justify-center">
        <div className="ra-glass rounded-lg shadow-[0_20px_25px_rgba(0,0,0,0.4)] p-8 max-w-md w-full mx-4">
          <div className="text-red-500 text-center mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-[#e6f1ff] text-center mb-2">Error Loading Portfolio</h2>
          <p className="text-[#a8b2d1] text-center mb-6">{error}</p>
          <button
            onClick={fetchPortfolioData}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate enhanced metrics using dashboard data
  const enhancedStats = calculateEnhancedStats(companies);
  
  // Use backend stats for accurate counts
  const avgRiskScore = dashboardData?.summary_stats?.avg_risk_score || 0;
  let portfolioHealth = 'Unknown';
  if (avgRiskScore > 0) {
    if (avgRiskScore < 30) portfolioHealth = 'Excellent';
    else if (avgRiskScore < 50) portfolioHealth = 'Good';
    else if (avgRiskScore < 70) portfolioHealth = 'Needs Attention';
    else portfolioHealth = 'Critical';
  }
  
  const statsWithBackendData = {
    ...enhancedStats,
    companiesWithAssessments: dashboardData?.summary_stats?.completed_assessments || 0,
    averageRiskScore: avgRiskScore,
    portfolioHealth: portfolioHealth
  };

  return (
    <div className="min-h-screen bg-[#0a192f]">
      
      {/* Header */}
      <div className="ra-glass shadow-[0_1px_3px_rgba(0,0,0,0.3)] border-b border-[rgba(255,255,255,0.12)] mb-8">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#e6f1ff]">{roleLabels.dashboardTitle}</h1>
                {enterpriseMode && (
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200 border border-purple-400/40">
                    Enterprise mode
                  </span>
                )}
              </div>
              <p className="text-[#a8b2d1]">
                {enterpriseMode
                  ? `${dashboardData?.organization?.name || 'Enterprise'} — ${roleLabels.subtitle}`
                  : `${dashboardData?.organization?.name || 'MSSP'} - ${dashboardData?.organization?.type?.toUpperCase() || 'PORTFOLIO'} ${roleLabels.clientSingular} Portfolio`}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={openBoardReport}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
                title="Board-ready roll-up + burn-down trend + PDF export"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h4m0 0l-4-4m4 4l-4 4M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
                </svg>
                Board Report
              </button>
              <button
                onClick={toggleEnterpriseMode}
                className={`flex items-center px-3 py-2 rounded-lg transition-colors shadow-[0_4px_24px_rgba(0,0,0,0.3)] text-sm ${
                  enterpriseMode
                    ? 'bg-purple-500/20 text-purple-200 border border-purple-400/40'
                    : 'bg-[rgba(255,255,255,0.08)] text-[#a8b2d1] border border-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.12)]'
                }`}
                title={enterpriseMode ? 'Switch to MSSP / PM view' : 'Switch to Enterprise CISO / BISO view'}
              >
                {enterpriseMode ? 'Enterprise' : 'MSSP'} view
              </button>
              {!enterpriseMode && (
                <button
                  onClick={handleCreatePortfolio}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Portfolio
                </button>
              )}
              <button
                onClick={fetchPortfolioData}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Data
              </button>
              <button
                onClick={() => navigate('/coordinator-dashboard')}
                className="flex items-center px-4 py-2 bg-[rgba(255,255,255,0.20)] text-white rounded-lg hover:bg-[#1e3148] transition-colors shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Main
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

        {/* Sprint 3 — Action Queue (prioritized work for the PM today) */}
        <ActionQueue
          drift={dashboardData?.prism_aggregates?.drift}
          topDriftClients={dashboardData?.prism_aggregates?.drift?.top_clients || []}
          perClientPosture={dashboardData?.prism_aggregates?.per_client_posture || []}
          outstandingRequests={outstandingRequests}
          companies={companies}
          collapsed={actionQueueCollapsed}
          onToggle={() => setActionQueueCollapsed((v) => !v)}
          onRefresh={fetchOutstandingRequests}
        />

        {/* Sprint 2 — Portfolio drift alert banner (Tile 8) */}
        {dashboardData?.prism_aggregates?.drift?.clients_with_open > 0 && (
          <div className={`rounded-xl border p-4 mb-6 flex items-start gap-3 ${
            dashboardData.prism_aggregates.drift.critical > 0 ? 'bg-red-500/10 border-red-500/40' :
            dashboardData.prism_aggregates.drift.high > 0     ? 'bg-orange-500/10 border-orange-500/40' :
                                                                 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex-1">
              <div className="text-base font-semibold text-[#e6f1ff]">
                {dashboardData.prism_aggregates.drift.clients_with_open} client
                {dashboardData.prism_aggregates.drift.clients_with_open === 1 ? '' : 's'} below SLA threshold
                {' · '}
                {dashboardData.prism_aggregates.drift.total_open} open drift
                {dashboardData.prism_aggregates.drift.total_open === 1 ? '' : 's'}
              </div>
              <div className="text-xs text-[#a8b2d1] mt-1">
                {[
                  dashboardData.prism_aggregates.drift.critical > 0 ? `${dashboardData.prism_aggregates.drift.critical} critical` : null,
                  dashboardData.prism_aggregates.drift.high > 0     ? `${dashboardData.prism_aggregates.drift.high} high`         : null,
                  dashboardData.prism_aggregates.drift.medium > 0   ? `${dashboardData.prism_aggregates.drift.medium} medium`     : null,
                  dashboardData.prism_aggregates.drift.low > 0      ? `${dashboardData.prism_aggregates.drift.low} low`           : null,
                ].filter(Boolean).join(' · ')}
              </div>
              {dashboardData.prism_aggregates.drift.top_clients?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {dashboardData.prism_aggregates.drift.top_clients.map((c) => (
                    <span key={c.foundations_company_id} className="text-xs px-2 py-1 rounded-md bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.12)] text-[#ccd6f6]">
                      {c.company_name || `Client #${c.foundations_company_id}`}: {c.open_count}
                      {c.max_severity ? ` (${c.max_severity})` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enhanced Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#a8b2d1]">Total {roleLabels.clientPlural}</p>
                <p className="text-3xl font-bold text-[#e6f1ff] mt-2">{dashboardData?.summary_stats?.total_clients || 0}</p>
                {/* Tile 7 — PRISM linkage coverage. Apr 20 Foundations Strategy Scrub. */}
                {(dashboardData?.summary_stats?.prism_linked_clients ?? 0) > 0 && (
                  <p className="text-xs text-[#8892b0] mt-1">
                    <span className="text-cyan-300 font-semibold">{dashboardData.summary_stats.prism_linked_clients}</span>
                    {` of ${dashboardData.summary_stats.total_clients} also run PRISM`}
                  </p>
                )}
                {(dashboardData?.summary_stats?.prism_linked_clients ?? 0) === 0 && (dashboardData?.summary_stats?.total_clients ?? 0) > 0 && (
                  <p className="text-xs text-[#8892b0] mt-1">No clients linked to PRISM yet</p>
                )}
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#a8b2d1]">Completed Assessments</p>
                <p className="text-3xl font-bold text-[#e6f1ff] mt-2">{dashboardData?.summary_stats?.completed_assessments || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#a8b2d1]">Revenue at Risk</p>
                <p className="text-3xl font-bold text-[#e6f1ff] mt-2">
                  {enhancedStats.totalRevenueAtRisk > 0 ? formatCurrency(enhancedStats.totalRevenueAtRisk) : 'Calculating...'}
                </p>
                <p className="text-xs text-[#8892b0] mt-1">
                  {dashboardData?.summary_stats?.completed_assessments > 0 
                    ? `From ${dashboardData.summary_stats.completed_assessments} completed assessments`
                    : 'Complete assessments to see data'
                  }
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] p-6 border-l-4 border-purple-500">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#a8b2d1] mb-3">Portfolio Risk Posture</p>
                <BandDistribution
                  scores={(companies || []).map(
                    c => c.risk_score ?? c.latest_assessment?.risk_score ?? null
                  )}
                  subtitle={
                    dashboardData?.summary_stats?.completed_assessments > 0
                      ? `${dashboardData.summary_stats.completed_assessments} of ${dashboardData.summary_stats.total_clients} assessed`
                      : 'Complete assessments to populate'
                  }
                />
              </div>
              <div className="p-3 bg-purple-100 rounded-full shrink-0">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Sprint 2 — Tile 9: SPRS distribution across portfolio */}
        {dashboardData?.prism_aggregates?.sprs && (dashboardData.prism_aggregates.sprs.distribution?.length > 0) && (
          <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] mb-8 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#e6f1ff]">SPRS Score Distribution</h3>
                <p className="text-sm text-[#a8b2d1]">DFARS 7012 posture across your PRISM-linked clients</p>
              </div>
              <div className="text-right">
                {dashboardData.prism_aggregates.sprs.avg_score != null && (
                  <>
                    <div className="text-xs text-[#a8b2d1]">Portfolio average</div>
                    <div className="text-2xl font-bold text-[#e6f1ff]">{dashboardData.prism_aggregates.sprs.avg_score}</div>
                  </>
                )}
                {dashboardData.prism_aggregates.sprs.submitted > 0 && (
                  <div className="text-[11px] mt-1 text-emerald-300">
                    {dashboardData.prism_aggregates.sprs.submitted} formally submitted
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { key: 'above_80',      label: '≥ 80 (Strong)',        cls: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' },
                { key: 'between_50_79', label: '50–79 (Moderate)',     cls: 'bg-amber-500/15 border-amber-500/40 text-amber-200'     },
                { key: 'between_0_49',  label: '0–49 (Weak)',          cls: 'bg-orange-500/15 border-orange-500/40 text-orange-200'  },
                { key: 'negative',      label: '< 0 (Significant gap)', cls: 'bg-red-500/15 border-red-500/40 text-red-200'          }
              ].map((bucket) => (
                <div key={bucket.key} className={`rounded-lg border px-4 py-3 ${bucket.cls}`}>
                  <div className="text-xs font-medium uppercase tracking-wide opacity-80">{bucket.label}</div>
                  <div className="text-3xl font-bold mt-1">{dashboardData.prism_aggregates.sprs[bucket.key] || 0}</div>
                  <div className="text-[11px] opacity-60 mt-1">clients</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enhanced Portfolio Health Metrics */}
        <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] mb-8 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-[#e6f1ff]">Portfolio Health Overview</h3>
            <p className="text-sm text-[#a8b2d1]">Comprehensive risk analysis across all client companies</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Portfolio Health Status */}
            <div className="ra-glass rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[#ccd6f6]">Overall Health</h4>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  statsWithBackendData.portfolioHealth === 'Excellent' ? 'bg-green-100 text-[#ccd6f6]' :
                  statsWithBackendData.portfolioHealth === 'Good' ? 'bg-blue-100 text-[#ccd6f6]' :
                  statsWithBackendData.portfolioHealth === 'Needs Attention' ? 'bg-yellow-100 text-[#ccd6f6]' :
                  statsWithBackendData.portfolioHealth === 'Critical' ? 'bg-red-100 text-[#ccd6f6]' :
                  'bg-[#172a45] text-[#e6f1ff]'
                }`}>
                  {statsWithBackendData.portfolioHealth}
                </span>
              </div>
              <div className="py-2">
                <BandDistribution
                  scores={(companies || []).map(
                    c => c.risk_score ?? c.latest_assessment?.risk_score ?? null
                  )}
                  compact
                />
              </div>
              <div className="text-xs text-[#8892b0] text-center">
                {dashboardData?.summary_stats?.completed_assessments > 0 
                  ? `Based on ${dashboardData.summary_stats.completed_assessments} of ${dashboardData.summary_stats.total_clients} companies`
                  : `${dashboardData?.summary_stats?.total_clients || 0} companies in portfolio (${(dashboardData?.summary_stats?.total_clients || 0) - (dashboardData?.summary_stats?.completed_assessments || 0)} pending assessment)`
                }
              </div>
            </div>

            {/* Risk Distribution */}
            <div className="ra-glass rounded-lg p-4">
              <h4 className="text-sm font-semibold text-[#ccd6f6] mb-3">Risk Distribution</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#a8b2d1]">Critical Risk</span>
                  <span className="text-sm font-bold text-red-600">{dashboardData?.summary_stats?.critical_risk_companies || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#a8b2d1]">High Risk</span>
                  <span className="text-sm font-bold text-orange-600">{dashboardData?.summary_stats?.high_risk_companies || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#a8b2d1]">Medium Risk</span>
                  <span className="text-sm font-bold text-yellow-600">{dashboardData?.summary_stats?.medium_risk_companies || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#a8b2d1]">Low Risk</span>
                  <span className="text-sm font-bold text-green-600">{dashboardData?.summary_stats?.low_risk_companies || 0}</span>
                </div>
              </div>
              {statsWithBackendData.totalCompanies > 0 && (
                <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.12)]">
                  <div className="flex h-2 rounded-full overflow-hidden bg-[#1e3148]">
                    {(dashboardData?.summary_stats?.low_risk_companies || 0) > 0 && (
                      <div 
                        className="bg-green-500" 
                        style={{width: `${((dashboardData?.summary_stats?.low_risk_companies || 0) / statsWithBackendData.companiesWithAssessments) * 100}%`}}
                      />
                    )}
                    {(dashboardData?.summary_stats?.medium_risk_companies || 0) > 0 && (
                      <div 
                        className="bg-yellow-500" 
                        style={{width: `${((dashboardData?.summary_stats?.medium_risk_companies || 0) / statsWithBackendData.companiesWithAssessments) * 100}%`}}
                      />
                    )}
                    {(dashboardData?.summary_stats?.high_risk_companies || 0) > 0 && (
                      <div 
                        className="bg-orange-500" 
                        style={{width: `${((dashboardData?.summary_stats?.high_risk_companies || 0) / statsWithBackendData.companiesWithAssessments) * 100}%`}}
                      />
                    )}
                    {(dashboardData?.summary_stats?.critical_risk_companies || 0) > 0 && (
                      <div 
                        className="bg-red-500" 
                        style={{width: `${((dashboardData?.summary_stats?.critical_risk_companies || 0) / statsWithBackendData.companiesWithAssessments) * 100}%`}}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Financial Impact */}
            <div className="ra-glass rounded-lg p-4">
              <h4 className="text-sm font-semibold text-[#ccd6f6] mb-3">Financial Impact</h4>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-[#a8b2d1]">Total Annual Revenue</div>
                  <div className="text-xl font-bold text-[#e6f1ff]">
                    {formatCurrency(enhancedStats.totalAnnualRevenue)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#a8b2d1]">Revenue at Risk %</div>
                  <div className="text-xl font-bold text-red-600">
                    {enhancedStats.totalAnnualRevenue > 0 
                      ? `${((enhancedStats.totalRevenueAtRisk / enhancedStats.totalAnnualRevenue) * 100).toFixed(1)}%`
                      : '--'}
                  </div>
                </div>
                <div className="pt-2 border-t border-[rgba(255,255,255,0.12)]">
                  <div className="text-xs text-[#a8b2d1]">Avg Recovery Time</div>
                  <div className="text-lg font-bold text-orange-600">
                    {enhancedStats.averageRecoveryDays} days
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Tabs Section */}
        <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] mb-8">
          <div className="border-b border-[rgba(255,255,255,0.12)]">
            <nav className="flex space-x-1 p-1" aria-label="Analytics Tabs">
              <button
                onClick={() => setAnalyticsView('financial')}
                className={`flex-1 py-3 px-4 text-center rounded-lg font-medium transition-colors ${
                  analyticsView === 'financial'
                    ? 'bg-blue-600 text-white'
                    : 'text-[#a8b2d1] hover:text-[#e6f1ff] hover:bg-[#172a45]'
                }`}
              >
                <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Financial Impact Analysis
              </button>
              <button
                onClick={() => setAnalyticsView('compliance')}
                className={`flex-1 py-3 px-4 text-center rounded-lg font-medium transition-colors ${
                  analyticsView === 'compliance'
                    ? 'bg-blue-600 text-white'
                    : 'text-[#a8b2d1] hover:text-[#e6f1ff] hover:bg-[#172a45]'
                }`}
              >
                <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Compliance Posture
              </button>
              <button
                onClick={() => setAnalyticsView('companies')}
                className={`flex-1 py-3 px-4 text-center rounded-lg font-medium transition-colors ${
                  analyticsView === 'companies'
                    ? 'bg-blue-600 text-white'
                    : 'text-[#a8b2d1] hover:text-[#e6f1ff] hover:bg-[#172a45]'
                }`}
              >
                <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Companies ({companies.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {analyticsView === 'financial' && (
              <FinancialImpactAnalysis companies={companies} portfolioId={selectedPortfolio?.id} />
            )}

            {analyticsView === 'compliance' && (
              <CompliancePostureTab
                aggregates={dashboardData?.prism_aggregates}
                totalLinked={dashboardData?.summary_stats?.prism_linked_clients || 0}
                totalClients={dashboardData?.summary_stats?.total_clients || 0}
                heatmap={outsidePressuresHeatmap}
                heatmapLoading={heatmapLoading}
                companies={companies}
              />
            )}
            
            {analyticsView === 'companies' && (
              <div>
                {/* Search and Sort Controls */}
                <div className="mb-6 flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search companies by name, industry, or access level..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search companies"
                        className="w-full px-4 py-2 pl-10 border border-[rgba(255,255,255,0.18)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgba(0,217,255,0.5)]"
                      />
                      <svg className="absolute left-3 top-2.5 w-5 h-5 text-[#5a6a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-4 py-2 border border-[rgba(255,255,255,0.18)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgba(0,217,255,0.5)]"
                    >
                      <option value="name">Sort by Name</option>
                      <option value="risk">Sort by Risk Score</option>
                      <option value="revenue">Sort by Revenue</option>
                      <option value="status">Sort by Completion</option>
                    </select>
                    
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="px-4 py-2 border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[rgba(255,255,255,0.03)] focus:outline-none focus:ring-2 focus:ring-[rgba(0,217,255,0.5)]"
                    >
                      {sortOrder === 'asc' ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Companies List */}
                <div className="space-y-4">
                  {getFilteredAndSortedCompanies().length === 0 ? (
                    <div className="text-center py-12">
                      <svg className="w-16 h-16 mx-auto text-[#5a6a8a] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <h3 className="text-lg font-medium text-[#e6f1ff] mb-2">
                        {searchTerm ? 'No companies found' : 'No companies in portfolio'}
                      </h3>
                      <p className="text-[#a8b2d1]">
                        {searchTerm ? `No companies match "${searchTerm}"` : 'Add companies to start managing your portfolio'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {getFilteredAndSortedCompanies().map((company) => (
                        <div key={company.company_id} className="ra-glass border border-[rgba(255,255,255,0.12)] rounded-lg hover:shadow-[0_4px_24px_rgba(0,0,0,0.3)] transition-shadow">
                          <div 
                            className="p-4 cursor-pointer"
                            onClick={() => handleViewCompanyDetails(company)}
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-[#e6f1ff]">{company.company_name}</h3>
                                {company.latest_assessment?.industry_vertical && (
                                  <p className="text-sm text-[#a8b2d1] mt-1">{company.latest_assessment.industry_vertical}</p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                {(() => {
                                  // Unfinished: the assessment's status, never a score (Pat, 2026-10-19).
                                  const badge = clientRiskBadge(company);
                                  if (badge.kind !== 'score') {
                                    return (
                                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#172a45] text-[#a8b2d1]">
                                        {badge.kind === 'status' ? badge.label : 'Risk: not measured'}
                                      </span>
                                    );
                                  }
                                  const style = getRiskBandStyle(badge.score);
                                  return (
                                    <span
                                      className="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1"
                                      style={{ background: style.bg, color: style.text, border: `1px solid ${style.border}` }}
                                    >
                                      <span aria-hidden="true">{style.icon}</span>
                                      <span>Risk: {style.label}</span>
                                    </span>
                                  );
                                })()}
                                {/* CRS Badge — band only, no numeric */}
                                {crsBatchData[company.company_id] && (() => {
                                  const crs = crsBatchData[company.company_id];
                                  // Prefer the legacy band string when present (server-computed),
                                  // otherwise derive from the numeric score.
                                  const bandKey = crs.band
                                    ? legacyCrsBandToKey(crs.band)
                                    : getRiskBand(crs.score ?? 0);
                                  const style = getRiskBandStyle(
                                    bandKey === 'critical' ? 100 :
                                    bandKey === 'high' ? 70 :
                                    bandKey === 'moderate' ? 45 : 15
                                  );
                                  return (
                                    <span
                                      className="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1"
                                      style={{ background: style.bg, color: style.text, border: `1px solid ${style.border}` }}
                                    >
                                      <span aria-hidden="true">{style.icon}</span>
                                      <span>Posture: {style.label}</span>
                                    </span>
                                  );
                                })()}
                                {company.completion_percentage !== undefined && (
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    company.completion_percentage === 100 ? 'bg-green-100 text-[#ccd6f6]' :
                                    company.completion_percentage > 0 ? 'bg-blue-100 text-[#ccd6f6]' :
                                    'bg-[#172a45] text-[#e6f1ff]'
                                  }`}>
                                    {company.completion_percentage}% Complete
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-[#a8b2d1]">Revenue:</span>
                                <span className="ml-2 font-medium">
                                  {company.annual_revenue || company.latest_assessment?.annual_revenue 
                                    ? formatCurrency(company.annual_revenue || company.latest_assessment?.annual_revenue)
                                    : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="text-[#a8b2d1]">Employees:</span>
                                <span className="ml-2 font-medium">
                                  {company.employee_count || company.latest_assessment?.employee_count || 'N/A'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.12)] flex justify-between items-center text-xs text-[#8892b0]">
                              <span>Access: {company.access_level?.replace('_', ' ')}</span>
                              <span>Added: {formatDate(company.granted_at)}</span>
                            </div>
                          </div>
                          {!enterpriseMode && (
                            <div className="px-4 pb-3 flex gap-2">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openRequestDialog(company); }}
                                className="flex-1 text-xs font-medium px-3 py-2 rounded-md border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 hover:border-cyan-400/60 transition-colors"
                              >
                                Request update
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openPlacementWizard(company); }}
                                className="flex-1 text-xs font-medium px-3 py-2 rounded-md border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-colors"
                                title="Place in portfolio with recommendation"
                              >
                                Place in portfolio
                              </button>
                            </div>
                          )}
                          {enterpriseMode && (
                            <div className="px-4 pb-3 text-[11px] text-[#8892b0] italic text-center">
                              Read-only · drill into branch for detail
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Portfolio Groups Section */}
        <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] mb-8">
          <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-[#e6f1ff]">Portfolio Groups</h2>
                <p className="text-[#a8b2d1] mt-1">{portfolios.length} portfolio{portfolios.length !== 1 ? 's' : ''} created</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {portfolios.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-[#5a6a8a] mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-[#e6f1ff] mb-2">No portfolios created yet</h3>
                <p className="text-[#a8b2d1] mb-6">Create your first portfolio to start organizing your clients</p>
                <button
                  onClick={handleCreatePortfolio}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Your First Portfolio
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {portfolios.map((portfolio) => (
                  <div key={portfolio.id} className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 hover:shadow-[0_8px_32px_rgba(0,217,255,0.12)] transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-[#e6f1ff]">{portfolio.name}</h3>
                        <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                          portfolio.type === 'portfolio' ? 'bg-blue-100 text-[#ccd6f6]' :
                          portfolio.type === 'custom' ? 'bg-purple-100 text-[#e6f1ff]' :
                          'bg-[#172a45] text-[#e6f1ff]'
                        }`}>
                          {portfolio.type || 'portfolio'}
                        </span>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          type="button"
                          onClick={() => handleViewPortfolioDetails(portfolio)}
                          className="p-1 text-[#5a6a8a] hover:text-blue-400 transition-colors"
                          aria-label="View portfolio"
                          title="View portfolio"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditPortfolio(portfolio)}
                          className="p-1 text-[#5a6a8a] hover:text-blue-400 transition-colors"
                          aria-label="Edit portfolio"
                          title="Edit portfolio (rename, capacity, membership)"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPortfolioToDelete(portfolio)}
                          className="p-1 text-[#5a6a8a] hover:text-red-400 transition-colors"
                          aria-label="Delete portfolio"
                          title="Delete portfolio"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {portfolio.description && (
                      <p className="text-sm text-[#a8b2d1] mb-3">{portfolio.description}</p>
                    )}

                    {/* FA-11-d — Balance view. Roger-model capacity vs load + key signals. */}
                    <PortfolioBalanceCard portfolio={portfolio} />

                    <div className="mt-4 pt-3 border-t border-[rgba(255,255,255,0.12)]">
                      <button
                        onClick={() => handleViewPortfolioDetails(portfolio)}
                        className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                        View Portfolio Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Portfolio Details Modal */}
      <Dialog.Root open={showPortfolioDetails && !!selectedPortfolio} onOpenChange={setShowPortfolioDetails}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black bg-opacity-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 ra-glass rounded-lg shadow-[0_20px_25px_rgba(0,0,0,0.4)] max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
              <div className="flex justify-between items-start">
                <div>
                  <Dialog.Title className="text-2xl font-bold text-[#e6f1ff]">{selectedPortfolio?.name}</Dialog.Title>
                  <span className={`inline-block mt-2 px-3 py-1 text-sm rounded-full ${
                    selectedPortfolio?.type === 'portfolio' ? 'bg-blue-100 text-[#ccd6f6]' :
                    selectedPortfolio?.type === 'custom' ? 'bg-purple-100 text-[#e6f1ff]' :
                    'bg-[#172a45] text-[#e6f1ff]'
                  }`}>
                    {selectedPortfolio?.type || 'portfolio'} portfolio
                  </span>
                </div>
                <Dialog.Close asChild>
                  <button
                    className="p-2 hover:bg-[#172a45] rounded-lg transition-colors"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </Dialog.Close>
              </div>
              {selectedPortfolio?.description && (
                <p className="mt-3 text-[#a8b2d1]">{selectedPortfolio.description}</p>
              )}
            </div>

            {/* Portfolio Companies List */}
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">Companies in Portfolio</h3>
                <div className="flex items-center justify-between">
                  <p className="text-[#a8b2d1]">
                    {selectedPortfolio?.companies?.length || 0} companies • Risk Threshold: {selectedPortfolio?.high_risk_threshold || 70}%
                  </p>
                </div>
              </div>

              {selectedPortfolio?.companies && selectedPortfolio.companies.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto">
                  {selectedPortfolio.companies.map((company) => {
                    // null when unmeasured (was `|| 0`, which opened the client modal on a "Low" badge).
                    const riskScore = measuredRiskScore(company);
                    
                    return (
                      <div key={company.company_id || company.id} className="ra-glass border border-[rgba(255,255,255,0.12)] rounded-lg hover:shadow-[0_4px_24px_rgba(0,0,0,0.3)] transition-shadow">
                        <div 
                          className="p-4 cursor-pointer"
                          onClick={() => {
                            setShowPortfolioDetails(false);
                            // Ensure we pass complete company data to the modal
                            const companyData = {
                              ...company,
                              id: company.company_id || company.id,
                              name: company.company_name || company.name,
                              risk_score: riskScore,
                              revenue: company.annual_revenue || company.revenue
                            };
                            setSelectedCompanyForModal(companyData);
                            setShowCompanyDetailModal(true);
                          }}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-[#e6f1ff]">{company.company_name || company.name}</h3>
                              {company.industry && (
                                <p className="text-sm text-[#8892b0] mt-1">{company.industry}</p>
                              )}
                            </div>
                            <div className="ml-4 flex flex-col items-end gap-1">
                              {(() => {
                                // Same badge as the main list: an unfinished client shows its
                                // assessment status, never a score (Pat, 2026-10-19).
                                const badge = clientRiskBadge(company);
                                if (badge.kind !== 'score') {
                                  return (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#172a45] text-[#a8b2d1]">
                                      {badge.kind === 'status' ? badge.label : 'Risk: not measured'}
                                    </span>
                                  );
                                }
                                const s = getRiskBandStyle(badge.score);
                                return (
                                  <span
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}
                                  >
                                    <span aria-hidden="true">{s.icon}</span>
                                    <span>Risk: {s.label}</span>
                                  </span>
                                );
                              })()}
                              {/* CRS Badge — band only, no numeric */}
                              {crsBatchData[company.company_id || company.id] && (() => {
                                const crs = crsBatchData[company.company_id || company.id];
                                const bandKey = crs.band ? legacyCrsBandToKey(crs.band) : getRiskBand(crs.score ?? 0);
                                const repScore = bandKey === 'critical' ? 100 : bandKey === 'high' ? 70 : bandKey === 'moderate' ? 45 : 15;
                                const s = getRiskBandStyle(repScore);
                                return (
                                  <span
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}
                                  >
                                    <span aria-hidden="true">{s.icon}</span>
                                    <span>Posture: {s.label}</span>
                                  </span>
                                );
                              })()}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-[#a8b2d1]">Assessment Progress:</span>
                              <span className="text-[#e6f1ff]">{company.completion_percentage || 0}%</span>
                            </div>
                            <div className="w-full bg-[#1e3148] rounded-full h-1.5">
                              <div 
                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                                style={{ width: `${company.completion_percentage || 0}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Shown only when answered (null otherwise; the route used to send $0). formatCurrency,
                              not a hand-rolled $X.XM, which read a real $26K as "$0.0M". */}
                          {company.annual_revenue != null && (
                            <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.12)]">
                              <div className="flex justify-between text-sm">
                                <span className="text-[#a8b2d1]">Annual Revenue:</span>
                                <span className="text-[#e6f1ff] font-medium">
                                  {formatCurrency(company.annual_revenue)}
                                </span>
                              </div>
                            </div>
                          )}

                          {company.employee_count && (
                            <div className="mt-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-[#a8b2d1]">Employees:</span>
                                <span className="text-[#e6f1ff]">{company.employee_count.toLocaleString()}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-[#5a6a8a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-[#e6f1ff]">No companies in portfolio</h3>
                  <p className="mt-1 text-sm text-[#8892b0]">Add companies to start managing your portfolio</p>
                </div>
              )}
            </div>

            {/* Portfolio Footer */}
            <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)] flex justify-end">
              <Dialog.Close asChild>
                <button
                  className="px-6 py-2 bg-[rgba(255,255,255,0.20)] text-white rounded-lg hover:bg-[#1e3148] transition-colors"
                >
                  Close
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Portfolio Wizard Modal */}
      <PortfolioWizard
        isOpen={showPortfolioWizard}
        onClose={handleClosePortfolioWizard}
        onComplete={handlePortfolioCreated}
        availableCompanies={companies}
      />

      {/* Insights-Style Modals */}
      <CompanyDetailModal
        company={selectedCompanyForModal}
        isOpen={showCompanyDetailModal}
        onClose={() => setShowCompanyDetailModal(false)}
        onActionClick={handleModalAction}
        prismPosture={
          selectedCompanyForModal
            ? (dashboardData?.prism_aggregates?.per_client_posture || []).find(
                (p) => p.foundations_company_id === selectedCompanyForModal.company_id
              ) || null
            : null
        }
        prismLinked={
          selectedCompanyForModal
            ? !!(selectedCompanyForModal.prism_linked)
            : false
        }
      />

      <RevenueCalculationModal
        company={selectedCompanyForModal}
        isOpen={showRevenueCalculationModal}
        onClose={() => setShowRevenueCalculationModal(false)}
      />

      {/* Portfolio group Edit dialog */}
      <Dialog.Root open={!!editingPortfolio} onOpenChange={(open) => { if (!open) setEditingPortfolio(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(94vw,760px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-0 text-[#e6f1ff] max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.12)]">
              <div>
                <Dialog.Title className="text-lg font-semibold">Edit portfolio</Dialog.Title>
                <Dialog.Description className="text-sm text-[#a8b2d1]">
                  Rename, adjust capacity, and swap members for <span className="text-[#e6f1ff] font-medium">{editingPortfolio?.name}</span>.
                </Dialog.Description>
              </div>
              <Dialog.Close className="text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" aria-label="Close">✕</Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Portfolio name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Seat capacity (max clients)</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.maxCompanies}
                    onChange={(e) => setEditForm((f) => ({ ...f, maxCompanies: e.target.value }))}
                    className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff]"
                    placeholder="e.g. 8"
                  />
                  <p className="text-[11px] text-[#8892b0] mt-1">Powers the Balance view. Leave blank to skip.</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-[#e6f1ff]">Members ({editForm.memberIds.length})</h4>
                  <span className="text-[11px] text-[#8892b0]">Click ✕ to remove · click + below to add</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto rounded-md border border-[rgba(255,255,255,0.08)] p-2 bg-[rgba(255,255,255,0.02)]">
                  {editForm.memberIds.length === 0 && (
                    <div className="col-span-full text-center text-xs text-[#a8b2d1] py-4">No members yet.</div>
                  )}
                  {editForm.memberIds.map((id) => {
                    const co = (companies || []).find((c) => c.company_id === id);
                    return (
                      <div key={id} className="flex items-center justify-between px-2 py-1.5 rounded bg-[rgba(255,255,255,0.04)] text-xs">
                        <span className="truncate text-[#e6f1ff]">{co?.company_name || `Client #${id}`}</span>
                        <button
                          type="button"
                          onClick={() => setEditForm((f) => ({ ...f, memberIds: f.memberIds.filter((i) => i !== id) }))}
                          className="text-[#8892b0] hover:text-red-300 ml-2"
                          title="Remove from portfolio"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-[#e6f1ff] mb-2">
                  Add from accessible clients
                  <span className="ml-2 text-[11px] text-[#8892b0] font-normal">
                    {(companies || []).filter((c) => !editForm.memberIds.includes(c.company_id)).length} available
                  </span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto rounded-md border border-[rgba(255,255,255,0.08)] p-2 bg-[rgba(255,255,255,0.02)]">
                  {(companies || [])
                    .filter((c) => !editForm.memberIds.includes(c.company_id))
                    .slice(0, 200)
                    .map((c) => (
                      <button
                        key={c.company_id}
                        type="button"
                        onClick={() => setEditForm((f) => ({ ...f, memberIds: [...f.memberIds, c.company_id] }))}
                        className="flex items-center justify-between px-2 py-1.5 rounded bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(16,185,129,0.1)] hover:border-emerald-400/30 border border-transparent text-xs text-left transition-colors"
                      >
                        <span className="truncate text-[#ccd6f6]">{c.company_name}</span>
                        <span className="text-emerald-300 ml-2">+</span>
                      </button>
                    ))}
                </div>
              </div>

              {editError && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{editError}</div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-[rgba(255,255,255,0.12)] flex justify-end gap-2">
              <Dialog.Close asChild>
                <button type="button" className="px-4 py-2 rounded-md text-sm text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" disabled={editSaving}>
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={saveEditPortfolio}
                disabled={editSaving || !editForm.name.trim()}
                className="px-4 py-2 rounded-md text-sm font-medium bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Delete-portfolio confirmation */}
      <Dialog.Root open={!!portfolioToDelete} onOpenChange={(open) => { if (!open) setPortfolioToDelete(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(92vw,460px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-6 text-[#e6f1ff]">
            <Dialog.Title className="text-lg font-semibold mb-2">Delete portfolio?</Dialog.Title>
            <Dialog.Description className="text-sm text-[#a8b2d1] mb-5">
              This removes the <span className="text-[#e6f1ff] font-medium">{portfolioToDelete?.name}</span> portfolio group and its
              company-membership rows. Client data and assessments are untouched — this only deletes the grouping.
            </Dialog.Description>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button type="button" className="px-4 py-2 rounded-md text-sm text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" disabled={portfolioDeleting}>
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={confirmDeletePortfolio}
                disabled={portfolioDeleting}
                className="px-4 py-2 rounded-md text-sm font-medium bg-red-500/20 border border-red-400/40 text-red-100 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {portfolioDeleting ? 'Deleting…' : 'Delete portfolio'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* FA-11-f — Board Report modal */}
      <Dialog.Root open={boardReportOpen} onOpenChange={setBoardReportOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(96vw,920px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-0 text-[#e6f1ff] max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.12)]">
              <div>
                <Dialog.Title className="text-lg font-semibold">Board Report</Dialog.Title>
                <Dialog.Description className="text-sm text-[#a8b2d1]">
                  {enterpriseMode ? 'Enterprise risk roll-up' : 'Portfolio risk roll-up'} ·
                  {` ${dashboardData?.summary_stats?.total_clients || 0} ${roleLabels.clientPlural.toLowerCase()}`} ·
                  {` as of ${new Date().toLocaleDateString()}`}
                </Dialog.Description>
              </div>
              <Dialog.Close className="text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" aria-label="Close">✕</Dialog.Close>
            </div>

            <div id="board-report-body" className="flex-1 overflow-y-auto px-6 py-4 space-y-5 bg-[#0a192f]">
              <BoardReportSummary
                dashboardData={dashboardData}
                outstandingRequests={outstandingRequests}
                roleLabels={roleLabels}
              />
              <BoardReportTrend trend={trendData} loading={trendLoading} />
              <BoardReportConcentration companies={companies} heatmap={outsidePressuresHeatmap} />
            </div>

            <div className="flex items-center justify-between px-6 py-3 border-t border-[rgba(255,255,255,0.12)]">
              <div className="text-[11px] text-[#8892b0]">
                {trendData?.buckets?.length ? `Trend uses ${trendData.buckets.length} week(s) of snapshot history` : 'Trend requires historical Foundations snapshots'}
              </div>
              <div className="flex gap-2">
                <Dialog.Close asChild>
                  <button type="button" className="px-4 py-2 rounded-md text-sm text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors">
                    Close
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
                        import('html2canvas'),
                        import('jspdf')
                      ]);
                      const el = document.getElementById('board-report-body');
                      if (!el) return;
                      const canvas = await html2canvas(el, { backgroundColor: '#0a192f', scale: 2 });
                      const img = canvas.toDataURL('image/png');
                      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
                      const pageWidth = pdf.internal.pageSize.getWidth();
                      const pageHeight = pdf.internal.pageSize.getHeight();
                      const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
                      const w = canvas.width * ratio;
                      const h = canvas.height * ratio;
                      pdf.addImage(img, 'PNG', (pageWidth - w) / 2, 20, w, h);
                      pdf.save(`board-report-${new Date().toISOString().slice(0, 10)}.pdf`);
                    } catch (err) {
                      console.error('[board-report] export failed:', err);
                    }
                  }}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-purple-500/20 border border-purple-400/40 text-purple-100 hover:bg-purple-500/30 transition-colors"
                >
                  Export PDF
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* FA-11-b — Onboarding Placement wizard */}
      <Dialog.Root open={!!placementClient} onOpenChange={(open) => { if (!open) { setPlacementClient(null); setPlacementRecommendation(null); } }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(94vw,880px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-0 text-[#e6f1ff] max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.12)]">
              <div>
                <Dialog.Title className="text-lg font-semibold">Placement recommendation</Dialog.Title>
                <Dialog.Description className="text-sm text-[#a8b2d1]">
                  Which portfolio does <span className="text-[#e6f1ff] font-medium">{placementClient?.company_name}</span> best fit?
                  Compare current load, diversification, and journey signal across your portfolios.
                </Dialog.Description>
              </div>
              <Dialog.Close className="text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" aria-label="Close">✕</Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Client risk snapshot */}
              {placementRecommendation?.client && (
                <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.04)] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-[#a8b2d1] uppercase tracking-wide">Client profile</div>
                      <div className="text-sm font-semibold text-[#e6f1ff] mt-0.5">
                        {placementRecommendation.client.company_name}
                        {placementRecommendation.client.industry && (
                          <span className="ml-2 text-[#a8b2d1] font-normal">· {placementRecommendation.client.industry}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {placementRecommendation.client.risk_score != null && (
                        <div><span className="text-[#8892b0]">Risk</span> <span className="font-semibold text-[#e6f1ff] ml-1">{placementRecommendation.client.risk_score}</span></div>
                      )}
                      {placementRecommendation.client.sprs_score != null && (
                        <div><span className="text-[#8892b0]">SPRS</span> <span className="font-semibold text-[#e6f1ff] ml-1">{placementRecommendation.client.sprs_score}</span></div>
                      )}
                      {placementRecommendation.client.ato_step != null && (
                        <div><span className="text-[#8892b0]">ATO</span> <span className="font-semibold text-[#e6f1ff] ml-1">Step {placementRecommendation.client.ato_step}</span></div>
                      )}
                      {placementRecommendation.client.is_on_journey && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/40">On journey</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {placementLoading && (
                <div className="py-12 text-center text-sm text-[#a8b2d1]">Computing placement recommendations…</div>
              )}

              {!placementLoading && placementRecommendation?.groups?.length === 0 && (
                <div className="py-12 text-center text-sm text-[#a8b2d1]">
                  No portfolios yet. Create a portfolio first, then place clients into it.
                </div>
              )}

              {/* Group recommendation cards */}
              {!placementLoading && placementRecommendation?.groups?.length > 0 && (
                <div className="space-y-3">
                  {placementRecommendation.groups
                    .slice()
                    .sort((a, b) => (b.rec.overall_fit_score || 0) - (a.rec.overall_fit_score || 0))
                    .map((g) => {
                      const isSubmitting = placementSubmittingId === g.id;
                      const disabled = g.is_member_already || isSubmitting || !!placementSuccess;

                      return (
                        <div
                          key={g.id}
                          className={`rounded-lg border p-4 ${
                            g.rec.would_exceed_capacity
                              ? 'border-red-500/30 bg-red-500/5'
                              : g.rec.best_load_balance || g.rec.best_diversification
                              ? 'border-emerald-400/40 bg-emerald-500/5'
                              : 'border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)]'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3 gap-3">
                            <div>
                              <div className="text-sm font-semibold text-[#e6f1ff]">
                                {g.name}
                                <span className="ml-2 text-xs text-[#8892b0] font-normal">{g.type || 'portfolio'}</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {g.is_member_already && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-200 border border-slate-400/30">Already a member</span>
                                )}
                                {g.rec.best_load_balance && !g.is_member_already && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">Best load balance</span>
                                )}
                                {g.rec.best_diversification && !g.is_member_already && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-400/40">Best diversification</span>
                                )}
                                {g.rec.would_exceed_capacity && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-200 border border-red-400/40">Would exceed capacity</span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => confirmPlacement(g.id)}
                              disabled={disabled}
                              className="shrink-0 text-xs px-3 py-1.5 rounded-md border border-emerald-400/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmitting ? 'Placing…' : g.is_member_already ? 'In portfolio' : 'Place here'}
                            </button>
                          </div>

                          {/* Current vs projected stat grid */}
                          <div className="grid grid-cols-4 gap-3 text-xs">
                            <StatDelta label="Members" current={g.current.member_count} projected={g.projected.member_count} capacity={g.current.capacity} />
                            <StatDelta label="Avg risk" current={g.current.avg_risk_score} projected={g.projected.avg_risk_score} />
                            <StatDelta label="Industries" current={g.current.distinct_industries} projected={g.projected.distinct_industries} />
                            <StatDelta label="On journey" current={g.current.journey_clients} projected={g.projected.journey_clients} />
                          </div>

                          {g.rec.rationale?.length > 0 && (
                            <div className="mt-3 text-[11px] text-[#a8b2d1]">
                              {g.rec.rationale.map((r, i) => (
                                <div key={i} className="flex items-start gap-1.5">
                                  <span className="text-emerald-300">·</span>
                                  <span>{r}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              {placementError && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{placementError}</div>
              )}
              {placementSuccess && (
                <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2">{placementSuccess}</div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Tile 15 — PM → Coordinator request dialog */}
      <Dialog.Root open={!!requestDialogCompany} onOpenChange={(open) => { if (!open) setRequestDialogCompany(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(92vw,520px)] ra-glass rounded-xl border border-[rgba(255,255,255,0.12)] shadow-2xl p-6 text-[#e6f1ff] max-h-[90vh] overflow-y-auto">
            <Dialog.Title className="text-lg font-semibold mb-1">Request assessment update</Dialog.Title>
            <Dialog.Description className="text-sm text-[#a8b2d1] mb-5">
              {requestDialogCompany?.company_name
                ? `Asking ${requestDialogCompany.company_name}'s coordinator to take an action. You will not see their response to individual questions, only that the request was handled.`
                : ''}
            </Dialog.Description>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Type of request</label>
                <select
                  value={requestForm.request_type}
                  onChange={(e) => setRequestForm((f) => ({ ...f, request_type: e.target.value }))}
                  className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff]"
                >
                  <option value="update_assessment">Update an existing assessment</option>
                  <option value="complete_assessment">Complete an unfinished assessment</option>
                  <option value="clarify_response">Clarify a specific response</option>
                  <option value="schedule_review">Schedule a review / working session</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Priority</label>
                  <select
                    value={requestForm.priority}
                    onChange={(e) => setRequestForm((f) => ({ ...f, priority: e.target.value }))}
                    className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff]"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Due date (optional)</label>
                  <input
                    type="date"
                    value={requestForm.due_date}
                    onChange={(e) => setRequestForm((f) => ({ ...f, due_date: e.target.value }))}
                    className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#a8b2d1] mb-1">Message</label>
                <textarea
                  rows={5}
                  value={requestForm.request_message}
                  onChange={(e) => setRequestForm((f) => ({ ...f, request_message: e.target.value }))}
                  placeholder="Describe what you need from the client coordinator. Be specific so they can respond without back-and-forth."
                  className="w-full bg-[#0a1929] border border-[rgba(255,255,255,0.15)] rounded-md px-3 py-2 text-sm text-[#e6f1ff] placeholder:text-[#64748b]"
                />
              </div>

              {requestError && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{requestError}</div>
              )}
              {requestSuccess && (
                <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2">{requestSuccess}</div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Dialog.Close asChild>
                <button className="px-4 py-2 rounded-md text-sm text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors" disabled={requestSubmitting}>
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={submitRequest}
                disabled={requestSubmitting || !requestForm.request_message.trim()}
                className="px-4 py-2 rounded-md text-sm font-medium bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {requestSubmitting ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FA-11-d — Portfolio Balance card (Roger model)
// Capacity/load visualization per portfolio group. Two bars:
//   (1) Headcount utilization — members / max_companies
//   (2) Risk load utilization — (sum risk + journey bonus) / (max_companies × 100)
// Plus a 4-stat strip (avg risk, high-risk count, on-journey count, industries)
// and up to 3 top-load member chips so the PM can see at-a-glance what's
// driving the load.
// ─────────────────────────────────────────────────────────────────────────────
const PortfolioBalanceCard = ({ portfolio }) => {
  const b = portfolio.balance;
  if (!b) {
    // Fallback if backend didn't send balance block (older response shape)
    return (
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-[#8892b0]">Companies:</span>
          <span className="font-medium">{portfolio.company_count || 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#8892b0]">Created:</span>
          <span className="font-medium">{new Date(portfolio.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    );
  }

  const utilColor = (pct) => {
    if (pct == null) return 'bg-slate-500/50';
    if (pct >= 100)  return 'bg-red-500';
    if (pct >= 90)   return 'bg-orange-500';
    if (pct >= 70)   return 'bg-amber-500';
    return             'bg-emerald-500';
  };
  const pctText = (pct) => (pct == null ? '—' : `${Math.round(pct)}%`);

  return (
    <div className="space-y-3">
      {/* Utilization bars — only render if max_companies is set */}
      {portfolio.max_companies ? (
        <>
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[11px] uppercase tracking-wide text-[#8892b0]">Headcount</span>
              <span className="text-xs text-[#a8b2d1]">
                <span className="text-[#e6f1ff] font-semibold">{b.top_members ? portfolio.company_count : 0}</span>
                {` / ${portfolio.max_companies} · ${pctText(b.headcount_utilization_pct)}`}
              </span>
            </div>
            <div className="h-2 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
              <div
                className={`h-full ${utilColor(b.headcount_utilization_pct)} transition-all`}
                style={{ width: `${Math.min(100, b.headcount_utilization_pct ?? 0)}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[11px] uppercase tracking-wide text-[#8892b0]">Risk load</span>
              <span className="text-xs text-[#a8b2d1]">
                <span className="text-[#e6f1ff] font-semibold">{pctText(b.load_utilization_pct)}</span>
                <span className="text-[#8892b0] ml-1">of capacity</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
              <div
                className={`h-full ${utilColor(b.load_utilization_pct)} transition-all`}
                style={{ width: `${Math.min(100, b.load_utilization_pct ?? 0)}%` }}
              />
            </div>
            {b.load_utilization_pct != null && b.load_utilization_pct > 100 && (
              <p className="text-[11px] text-red-300 mt-1">Over-allocated — consider reassigning a client</p>
            )}
          </div>
        </>
      ) : (
        <div className="text-xs text-[#8892b0] italic">
          Set a seat capacity on this portfolio to see load balance.
          <span className="block text-[#64748b] mt-0.5">{portfolio.company_count} client{portfolio.company_count === 1 ? '' : 's'} currently assigned</span>
        </div>
      )}

      {/* Stat strip — Avg risk is banded; the other three are hard counts */}
      <div className="grid grid-cols-4 gap-1 text-center pt-2 border-t border-[rgba(255,255,255,0.06)]">
        <BalanceStatBand label="Avg risk" score={b.avg_risk_score} />
        <BalanceStat label="High risk" value={b.high_risk_count} highlight={b.high_risk_count > 0 ? 'warn' : null} />
        <BalanceStat label="On journey" value={b.journey_client_count} highlight={b.journey_client_count > 0 ? 'good' : null} />
        <BalanceStat label="Industries" value={b.distinct_industries} />
      </div>

      {/* Top-load members — chip color carries the risk band; numeric dropped */}
      {b.top_members && b.top_members.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-[#8892b0] mb-1.5">Top load</div>
          <div className="flex flex-wrap gap-1.5">
            {b.top_members.map((m) => {
              const style = m.risk_score != null ? getRiskBandStyle(m.risk_score) : null;
              const titleSuffix = m.industry ? ` · ${m.industry}` : '';
              const journeySuffix = m.on_journey ? ' · on journey' : '';
              const bandSuffix = style ? ` · ${style.label} risk` : '';
              return (
                <span
                  key={m.company_id}
                  className="text-[11px] px-2 py-0.5 rounded-md border inline-flex items-center gap-1"
                  style={style ? {
                    background: style.bg,
                    borderColor: style.border,
                    color: style.text,
                  } : {
                    background: 'rgba(100, 116, 139, 0.1)',
                    borderColor: 'rgba(148, 163, 184, 0.2)',
                    color: '#cbd5e1',
                  }}
                  title={`${m.company_name}${titleSuffix}${bandSuffix}${journeySuffix}`}
                >
                  {style && <span aria-hidden="true">{style.icon}</span>}
                  <span>{m.company_name}</span>
                  {m.on_journey && <span aria-label="on journey">◆</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const BalanceStat = ({ label, value, highlight }) => {
  const valueColor =
    highlight === 'warn' ? 'text-amber-300' :
    highlight === 'good' ? 'text-emerald-300' :
                          'text-[#e6f1ff]';
  return (
    <div>
      <div className="text-[10px] text-[#8892b0] uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold ${valueColor}`}>{value}</div>
    </div>
  );
};

// Banded variant — for squishy 0-100 scores (e.g. avg risk). Renders the band
// label + shape icon instead of the raw numeric, color-blind-safe.
const BalanceStatBand = ({ label, score }) => {
  if (score == null) {
    return (
      <div>
        <div className="text-[10px] text-[#8892b0] uppercase tracking-wide">{label}</div>
        <div className="text-sm font-semibold text-[#5a6a8a]">—</div>
      </div>
    );
  }
  const style = getRiskBandStyle(score);
  return (
    <div>
      <div className="text-[10px] text-[#8892b0] uppercase tracking-wide">{label}</div>
      <div className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: style.text }}>
        <span aria-hidden="true">{style.icon}</span>
        <span>{style.label}</span>
      </div>
    </div>
  );
};

// FA-11-b helper — tiny current-vs-projected stat with delta arrow
const StatDelta = ({ label, current, projected, capacity }) => {
  const cur = current ?? '—';
  const proj = projected ?? '—';
  const changed = current != null && projected != null && current !== projected;
  const direction = changed
    ? (Number(projected) > Number(current) ? 'up' : 'down')
    : null;
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '';
  const arrowColor = direction === 'up' ? 'text-amber-300' : direction === 'down' ? 'text-emerald-300' : 'text-[#8892b0]';
  return (
    <div className="rounded-md bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] px-3 py-2">
      <div className="text-[10px] text-[#8892b0] uppercase tracking-wide">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1.5">
        <span className="text-[#a8b2d1]">{cur}</span>
        <span className={arrowColor}>{arrow}</span>
        <span className="text-[#e6f1ff] font-semibold">{proj}</span>
        {capacity && <span className="text-[10px] text-[#64748b]">/{capacity}</span>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FA-11-a — Weekly Portfolio Scan
// Previously "Action Queue" (point-in-time state). Reframed per Apr 21 scrub
// to the weekly-moment pattern: "what moved against you since last Tuesday."
// Every item is tied to an event that happened in the last 7 days — new drift,
// new requests, newly-stale assessments, newly-onboarded clients. Persistent
// state (SPRS <50 across all time) is handled on the Compliance Posture tab's
// per-client table, not here.
// ─────────────────────────────────────────────────────────────────────────────
const WEEKLY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const STALE_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

const ActionQueue = ({
  drift,
  topDriftClients,
  // perClientPosture currently unused; wired when per-client week-over-week
  // posture signals land. Kept in the prop signature so the parent call site
  // doesn't need to change when we re-enable the item type.
  perClientPosture: _perClientPosture, // eslint-disable-line no-unused-vars
  outstandingRequests,
  companies,
  collapsed,
  onToggle,
  onRefresh
}) => {
  const now = Date.now();
  const windowStart = new Date(now - WEEKLY_WINDOW_MS);
  const items = [];

  // 1) New drift events — drift detected within the 7-day window
  for (const c of topDriftClients || []) {
    if (!c.latest_detected_at) continue;
    const detectedMs = new Date(c.latest_detected_at).getTime();
    if (now - detectedMs > WEEKLY_WINDOW_MS) continue;
    const sev = c.max_severity || 'medium';
    const priority =
      sev === 'critical' ? 'urgent' :
      sev === 'high'     ? 'high'   :
      sev === 'medium'   ? 'normal' : 'low';
    items.push({
      key: `drift-${c.foundations_company_id}`,
      type: 'drift',
      priority,
      sortTime: detectedMs,
      title: `${c.open_count} new drift${c.open_count === 1 ? '' : 's'} — ${c.company_name || `Client #${c.foundations_company_id}`}`,
      subtitle: `Detected ${new Date(c.latest_detected_at).toLocaleDateString()} · max severity ${sev}`,
      actionLabel: 'Review client',
      companyId: c.foundations_company_id
    });
  }

  // 2) New requests this week (created within window and still open)
  for (const r of outstandingRequests || []) {
    if (!r.created_at) continue;
    const createdMs = new Date(r.created_at).getTime();
    if (now - createdMs > WEEKLY_WINDOW_MS) continue;
    const priority = r.priority === 'urgent' ? 'urgent'
                   : r.priority === 'high'   ? 'high'
                   : r.status    === 'pending' ? 'normal'
                   : 'low';
    const statusCopy = r.status === 'pending'      ? 'Awaiting acknowledgement'
                    : r.status === 'acknowledged' ? 'Acknowledged — awaiting response'
                    : r.status === 'in_progress'  ? 'Coordinator working on it'
                    : r.status;
    items.push({
      key: `req-${r.id}`,
      type: 'request',
      priority,
      sortTime: createdMs,
      title: `${r.client_company_name || `Client #${r.client_company_id}`} — ${r.request_type.replace(/_/g, ' ')}`,
      subtitle: `Sent ${new Date(r.created_at).toLocaleDateString()} · ${statusCopy}`,
      actionLabel: null
    });
  }

  // 3) Assessments that went stale this week — last updated 14–21 days ago,
  //    i.e. they tipped over the stale threshold inside the 7-day window.
  for (const co of companies || []) {
    const pct = Number(co.completion_percentage || 0);
    if (pct === 0 || pct === 100) continue;
    const updated = co.last_updated || co.updated_at || co.latest_assessment?.updated_at;
    if (!updated) continue;
    const ageMs = now - new Date(updated).getTime();
    const wentStaleThisWeek = ageMs >= STALE_THRESHOLD_MS && ageMs <= STALE_THRESHOLD_MS + WEEKLY_WINDOW_MS;
    if (!wentStaleThisWeek) continue;
    items.push({
      key: `stale-${co.company_id}`,
      type: 'stale',
      priority: 'normal',
      sortTime: new Date(updated).getTime(),
      title: `${co.company_name} — assessment went stale`,
      subtitle: `${pct}% complete, last touched ${new Date(updated).toLocaleDateString()}`,
      actionLabel: 'Request update',
      companyId: co.company_id
    });
  }

  // 4) Newly onboarded clients this week — granted_at inside window
  for (const co of companies || []) {
    if (!co.granted_at) continue;
    const grantedMs = new Date(co.granted_at).getTime();
    if (now - grantedMs > WEEKLY_WINDOW_MS) continue;
    items.push({
      key: `onboard-${co.company_id}`,
      type: 'onboard',
      priority: 'low',
      sortTime: grantedMs,
      title: `${co.company_name} — newly onboarded`,
      subtitle: `Added ${new Date(co.granted_at).toLocaleDateString()} · consider portfolio placement`,
      actionLabel: 'Place in portfolio',
      companyId: co.company_id
    });
  }

  // ── sort by priority, then recency ───────────────────────────────
  const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
  items.sort((a, b) => {
    const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    return p !== 0 ? p : (b.sortTime || 0) - (a.sortTime || 0);
  });
  const totalCount = items.length;
  const urgentCount = items.filter((i) => i.priority === 'urgent').length;
  const highCount = items.filter((i) => i.priority === 'high').length;

  const typeIcon = (t) => ({
    drift:   '⚠️',
    request: '✉️',
    stale:   '⏳',
    onboard: '✨'
  })[t] || '•';
  const priorityColor = (p) => ({
    urgent: 'bg-red-500/20 text-red-200 border-red-400/40',
    high:   'bg-orange-500/20 text-orange-200 border-orange-400/40',
    normal: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40',
    low:    'bg-slate-500/20 text-slate-200 border-slate-400/40'
  })[p];

  const windowLabel = `since ${windowStart.toLocaleDateString()}`;

  return (
    <div className="ra-glass rounded-xl shadow-[0_8px_32px_rgba(0,217,255,0.12)] mb-6 border-l-4 border-cyan-500 overflow-hidden">
      <div className="px-5 py-3 flex items-center justify-between border-b border-[rgba(255,255,255,0.1)]">
        <button type="button" onClick={onToggle} className="flex items-center gap-3 text-left">
          <span className="text-cyan-300">▌</span>
          <div>
            <div className="text-sm font-semibold text-[#e6f1ff]">
              Weekly Portfolio Scan
              <span className="ml-2 text-xs font-medium text-[#a8b2d1]">
                ({totalCount} movement{totalCount === 1 ? '' : 's'} {windowLabel})
              </span>
            </div>
            <div className="text-[11px] text-[#8892b0]">
              {totalCount === 0
                ? <span>No movement in the last 7 days — portfolio is stable</span>
                : <>
                    {urgentCount > 0 && <span className="text-red-300">{urgentCount} urgent</span>}
                    {urgentCount > 0 && highCount > 0 && <span>{' · '}</span>}
                    {highCount > 0 && <span className="text-orange-300">{highCount} high</span>}
                    {urgentCount === 0 && highCount === 0 && <span>What changed against you this week</span>}
                  </>
              }
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            className="text-[11px] text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors px-2 py-1 rounded-md"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={onToggle}
            className="text-xs text-[#a8b2d1] hover:text-[#e6f1ff] transition-colors"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {totalCount === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-[#a8b2d1]">
              Nothing moved against you in the last 7 days. Check back next week.
            </div>
          ) : (
            <div className="divide-y divide-[rgba(255,255,255,0.06)] max-h-96 overflow-y-auto">
              {items.slice(0, 15).map((item) => (
                <div key={item.key} className="px-5 py-3 flex items-center gap-4 hover:bg-[rgba(255,255,255,0.02)]">
                  <div className="text-lg">{typeIcon(item.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#e6f1ff] truncate">{item.title}</div>
                    <div className="text-[11px] text-[#8892b0] truncate">{item.subtitle}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${priorityColor(item.priority)} shrink-0`}>
                    {item.priority}
                  </span>
                  {item.actionLabel && (
                    <button
                      type="button"
                      className="text-[11px] px-3 py-1 rounded-md border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 transition-colors shrink-0"
                      title="Scroll to Companies tab to take action"
                    >
                      {item.actionLabel}
                    </button>
                  )}
                </div>
              ))}
              {totalCount > 15 && (
                <div className="px-5 py-2 text-center text-[11px] text-[#8892b0]">
                  + {totalCount - 15} more — showing the 15 most significant this week
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sprint 3 — Tile 10: Compliance Posture tab
// The MSSP / insurance / DIB prime view of ATO stage, SPRS distribution,
// and DFARS readiness across the whole portfolio, plus a per-client table.
// ─────────────────────────────────────────────────────────────────────────────
const ATO_STEP_LABELS = {
  1: 'Categorize', 2: 'Select', 3: 'Implement',
  4: 'Assess',    5: 'Authorize', 6: 'Monitor'
};

const CompliancePostureTab = ({ aggregates, totalLinked, totalClients, heatmap, heatmapLoading, companies }) => {
  const sprs  = aggregates?.sprs  || {};
  const drift = aggregates?.drift || {};
  const ato   = aggregates?.ato   || {};
  const posture = Array.isArray(aggregates?.per_client_posture) ? aggregates.per_client_posture : [];

  if (totalLinked === 0) {
    return (
      <div className="py-16 text-center text-[#a8b2d1]">
        <div className="text-lg font-semibold text-[#e6f1ff] mb-2">No PRISM-linked clients yet</div>
        <p className="text-sm">
          This tab lights up as your clients' Foundations assessments get bridged to a PRISM project.
          All {totalClients} portfolio client{totalClients === 1 ? '' : 's'} {totalClients === 1 ? 'is' : 'are'} currently Foundations-only.
        </p>
      </div>
    );
  }

  const dfarsSubmittedPct = totalLinked > 0 ? Math.round(((sprs.submitted || 0) / totalLinked) * 100) : 0;
  const maxStepCount = Math.max(1, ...Object.values(ato.step_distribution || { 1: 0 }).map(Number));

  const severityBadge = (sev) => {
    const map = {
      critical: 'bg-red-500/20 text-red-200 border-red-400/40',
      high:     'bg-orange-500/20 text-orange-200 border-orange-400/40',
      medium:   'bg-amber-500/20 text-amber-200 border-amber-400/40',
      low:      'bg-slate-500/20 text-slate-200 border-slate-400/40'
    };
    return map[sev] || 'bg-slate-500/10 text-slate-300 border-slate-500/20';
  };
  const sprsBandColor = (score) => {
    if (score == null) return 'text-[#8892b0]';
    if (score >= 80)   return 'text-emerald-300';
    if (score >= 50)   return 'text-amber-300';
    if (score >= 0)    return 'text-orange-300';
    return 'text-red-300';
  };

  return (
    <div className="space-y-6">
      {/* Top KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
          <div className="text-xs font-medium text-[#a8b2d1] uppercase tracking-wide">Avg SPRS</div>
          <div className="text-3xl font-bold text-[#e6f1ff] mt-1">{sprs.avg_score ?? '—'}</div>
          <div className="text-xs text-[#8892b0] mt-1">{totalLinked} PRISM-linked client{totalLinked === 1 ? '' : 's'}</div>
        </div>
        <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
          <div className="text-xs font-medium text-[#a8b2d1] uppercase tracking-wide">DFARS Submitted</div>
          <div className="text-3xl font-bold text-[#e6f1ff] mt-1">{sprs.submitted || 0} <span className="text-lg text-[#8892b0]">({dfarsSubmittedPct}%)</span></div>
          <div className="text-xs text-[#8892b0] mt-1">formally affirmed</div>
        </div>
        <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
          <div className="text-xs font-medium text-[#a8b2d1] uppercase tracking-wide">ATO Journeys Active</div>
          <div className="text-3xl font-bold text-[#e6f1ff] mt-1">{ato.clients_with_journey || 0}</div>
          <div className="text-xs text-[#8892b0] mt-1">
            {ato.avg_percent != null ? `avg ${ato.avg_percent}% complete` : '—'}
            {ato.completed_journeys > 0 ? ` · ${ato.completed_journeys} completed` : ''}
          </div>
        </div>
        <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
          <div className="text-xs font-medium text-[#a8b2d1] uppercase tracking-wide">Open Drift</div>
          <div className="text-3xl font-bold text-[#e6f1ff] mt-1">{drift.total_open || 0}</div>
          <div className="text-xs text-[#8892b0] mt-1">
            {drift.clients_with_open || 0} client{drift.clients_with_open === 1 ? '' : 's'} affected
          </div>
        </div>
      </div>

      {/* ATO stage histogram */}
      <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-[#e6f1ff]">ATO Journey Stage Distribution</h4>
          <p className="text-xs text-[#a8b2d1]">Where each of your PRISM-linked clients sits in the 6-step authorization process</p>
        </div>
        <div className="grid grid-cols-6 gap-3 items-end" style={{ minHeight: 120 }}>
          {[1, 2, 3, 4, 5, 6].map((step) => {
            const count = Number((ato.step_distribution || {})[step]) || 0;
            const heightPct = Math.round((count / maxStepCount) * 100);
            return (
              <div key={step} className="flex flex-col items-center">
                <div className="text-xs font-semibold text-[#e6f1ff] mb-1">{count}</div>
                <div className="w-full bg-[rgba(255,255,255,0.04)] rounded-md" style={{ height: 80 }}>
                  <div
                    className="w-full rounded-md bg-gradient-to-t from-cyan-500/60 to-cyan-400/30 border-t border-cyan-300/40 transition-all"
                    style={{ height: `${heightPct}%`, marginTop: `${100 - heightPct}%` }}
                  />
                </div>
                <div className="text-[11px] text-[#a8b2d1] mt-2 text-center">
                  <div className="font-medium">Step {step}</div>
                  <div>{ATO_STEP_LABELS[step]}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SPRS distribution + drift severity side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
          <h4 className="text-sm font-semibold text-[#e6f1ff] mb-3">SPRS Score Buckets</h4>
          <div className="space-y-2">
            {[
              { key: 'above_80',      label: '≥ 80 (Strong)',        color: 'bg-emerald-400' },
              { key: 'between_50_79', label: '50–79 (Moderate)',     color: 'bg-amber-400' },
              { key: 'between_0_49',  label: '0–49 (Weak)',          color: 'bg-orange-400' },
              { key: 'negative',      label: '< 0 (Significant gap)', color: 'bg-red-400' }
            ].map((b) => {
              const n = Number(sprs[b.key] || 0);
              const pct = totalLinked > 0 ? Math.round((n / totalLinked) * 100) : 0;
              return (
                <div key={b.key} className="flex items-center gap-3">
                  <div className="text-xs text-[#a8b2d1] w-36">{b.label}</div>
                  <div className="flex-1 bg-[rgba(255,255,255,0.05)] rounded h-4 relative overflow-hidden">
                    <div className={`h-full ${b.color}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-[#e6f1ff] w-16 text-right">{n} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
          <h4 className="text-sm font-semibold text-[#e6f1ff] mb-3">Drift Severity Across Portfolio</h4>
          {(drift.total_open || 0) === 0 ? (
            <div className="py-8 text-center text-sm text-[#a8b2d1]">
              No open drift across your PRISM-linked clients.
            </div>
          ) : (
            <div className="space-y-2">
              {[
                { key: 'critical', label: 'Critical', color: 'bg-red-400' },
                { key: 'high',     label: 'High',     color: 'bg-orange-400' },
                { key: 'medium',   label: 'Medium',   color: 'bg-amber-400' },
                { key: 'low',      label: 'Low',      color: 'bg-slate-400' }
              ].map((b) => {
                const n = Number(drift[b.key] || 0);
                const pct = drift.total_open > 0 ? Math.round((n / drift.total_open) * 100) : 0;
                return (
                  <div key={b.key} className="flex items-center gap-3">
                    <div className="text-xs text-[#a8b2d1] w-20">{b.label}</div>
                    <div className="flex-1 bg-[rgba(255,255,255,0.05)] rounded h-4 relative overflow-hidden">
                      <div className={`h-full ${b.color}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-xs text-[#e6f1ff] w-12 text-right">{n}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tile 12 — Outside Pressures portfolio heatmap (industry × category) */}
      <OutsidePressuresHeatmap heatmap={heatmap} loading={heatmapLoading} />

      {/* FA-11-c — Concentration Risk (insurance underwriter variant) */}
      <ConcentrationRiskPanel companies={companies} posture={posture} heatmap={heatmap} />

      {/* Per-client posture table */}
      <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-[#e6f1ff]">Client-by-Client Compliance Posture</h4>
            <p className="text-xs text-[#a8b2d1]">Worst-case values aggregated across each client's PRISM projects</p>
          </div>
          <div className="text-xs text-[#8892b0]">
            {posture.length} of {totalLinked} linked client{totalLinked === 1 ? '' : 's'} with signal data
          </div>
        </div>
        {posture.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#a8b2d1] space-y-2">
            {totalLinked > 0 ? (
              <>
                <div>
                  <span className="font-medium text-[#e6f1ff]">{totalLinked}</span>
                  {` client${totalLinked === 1 ? ' is' : 's are'} linked to PRISM, but no signal data has been generated yet.`}
                </div>
                <div className="text-xs text-[#8892b0]">
                  This table fills as PRISM users calculate SPRS scores, advance the ATO Journey, or trigger compliance drift events on those projects.
                </div>
              </>
            ) : (
              <>
                <div>No clients are linked to PRISM yet.</div>
                <div className="text-xs text-[#8892b0]">
                  When a client's Foundations session is bridged to a PRISM project, their posture signals will appear here.
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#8892b0] border-b border-[rgba(255,255,255,0.08)]">
                  <th className="py-2 pr-4">Client</th>
                  <th className="py-2 px-4">SPRS</th>
                  <th className="py-2 px-4">DFARS 7012</th>
                  <th className="py-2 px-4">ATO Step</th>
                  <th className="py-2 px-4">Journey</th>
                  <th className="py-2 pl-4">Open Drift</th>
                </tr>
              </thead>
              <tbody>
                {posture
                  .slice()
                  .sort((a, b) => {
                    // Sort: clients with most open drift + lowest SPRS first (worst posture up top)
                    const driftDiff = (b.drift_open || 0) - (a.drift_open || 0);
                    if (driftDiff !== 0) return driftDiff;
                    const aScore = a.sprs_score ?? 999;
                    const bScore = b.sprs_score ?? 999;
                    return aScore - bScore;
                  })
                  .map((c) => (
                    <tr key={c.foundations_company_id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)]">
                      <td className="py-3 pr-4">
                        <div className="text-[#e6f1ff] font-medium">{c.company_name || `Client #${c.foundations_company_id}`}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-semibold ${sprsBandColor(c.sprs_score)}`}>
                          {c.sprs_score != null ? c.sprs_score : '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {c.sprs_submitted ? (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">Submitted</span>
                        ) : (
                          <span className="text-[11px] text-[#8892b0]">Not submitted</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {c.ato_step ? (
                          <span className="text-[#e6f1ff]">
                            {c.ato_step}. {ATO_STEP_LABELS[c.ato_step]}
                          </span>
                        ) : <span className="text-[#8892b0]">—</span>}
                      </td>
                      <td className="py-3 px-4">
                        {c.ato_percent != null ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-[rgba(255,255,255,0.05)] rounded-full h-1.5 w-20">
                              <div className="h-full rounded-full bg-cyan-400" style={{ width: `${Math.round(c.ato_percent)}%` }} />
                            </div>
                            <span className="text-xs text-[#a8b2d1]">{Math.round(c.ato_percent)}%</span>
                          </div>
                        ) : <span className="text-[#8892b0]">—</span>}
                      </td>
                      <td className="py-3 pl-4">
                        {c.drift_open > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="text-[#e6f1ff] font-semibold">{c.drift_open}</span>
                            {c.max_drift_severity && (
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${severityBadge(c.max_drift_severity)}`}>
                                {c.max_drift_severity}
                              </span>
                            )}
                          </div>
                        ) : <span className="text-[#8892b0]">—</span>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FA-11-f — Board Report sub-components
// ─────────────────────────────────────────────────────────────────────────────
const BoardReportSummary = ({ dashboardData, outstandingRequests, roleLabels }) => {
  const s = dashboardData?.summary_stats || {};
  const agg = dashboardData?.prism_aggregates || {};
  const openRequests = (outstandingRequests || []).length;
  const Kpi = ({ label, value, sublabel, tone }) => (
    <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-4">
      <div className="text-[10px] uppercase tracking-wide text-[#8892b0]">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone === 'warn' ? 'text-amber-300' : tone === 'bad' ? 'text-red-300' : 'text-[#e6f1ff]'}`}>{value}</div>
      {sublabel && <div className="text-[11px] text-[#8892b0] mt-0.5">{sublabel}</div>}
    </div>
  );
  return (
    <div>
      <h3 className="text-base font-semibold text-[#e6f1ff] mb-3">Executive Summary</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={`Total ${roleLabels.clientPlural.toLowerCase()}`} value={s.total_clients || 0} sublabel={`${s.completed_assessments || 0} with a completed assessment`} />
        <Kpi label="Avg risk score" value={s.avg_risk_score || '—'} sublabel="across completed assessments" tone={s.avg_risk_score >= 70 ? 'bad' : s.avg_risk_score >= 50 ? 'warn' : null} />
        <Kpi label="PRISM-linked" value={`${s.prism_linked_clients || 0}`} sublabel={`of ${s.total_clients || 0} ${roleLabels.clientPlural.toLowerCase()}`} />
        <Kpi label="Open drift" value={agg.drift?.total_open || 0} sublabel={`${agg.drift?.clients_with_open || 0} ${roleLabels.clientPlural.toLowerCase()} affected`} tone={(agg.drift?.total_open || 0) > 0 ? 'warn' : null} />
        <Kpi label="On journey" value={agg.ato?.clients_with_journey || 0} sublabel={agg.ato?.avg_percent != null ? `avg ${agg.ato.avg_percent}% complete` : '—'} />
        <Kpi label="Avg SPRS" value={agg.sprs?.avg_score ?? '—'} sublabel={`${agg.sprs?.submitted || 0} formally submitted`} />
        <Kpi label="Critical risk" value={s.critical_risk_companies || 0} sublabel={roleLabels.clientPlural.toLowerCase()} tone={s.critical_risk_companies > 0 ? 'bad' : null} />
        <Kpi label="Open requests" value={openRequests} sublabel="pending or in-progress" />
      </div>
    </div>
  );
};

const BoardReportTrend = ({ trend, loading }) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
        <div className="text-sm text-[#a8b2d1] animate-pulse">Loading portfolio trend…</div>
      </div>
    );
  }
  const buckets = trend?.buckets || [];
  if (buckets.length === 0) {
    return (
      <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
        <h3 className="text-base font-semibold text-[#e6f1ff] mb-1">Portfolio Trend</h3>
        <p className="text-sm text-[#a8b2d1]">No historical snapshots yet. Trend fills in as Foundations assessments are re-scored over time.</p>
      </div>
    );
  }

  // Build SVG line chart of avg_composite across weeks (lower = healthier)
  const padding = { top: 20, right: 30, bottom: 30, left: 40 };
  const width = 820;
  const height = 220;
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const xs = buckets.map((_, i) => i);
  const ys = buckets.map((b) => b.avg_composite).filter((y) => y != null);
  if (ys.length === 0) {
    return (
      <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
        <h3 className="text-base font-semibold text-[#e6f1ff] mb-1">Portfolio Trend</h3>
        <p className="text-sm text-[#a8b2d1]">Snapshots exist but contain no composite score data yet.</p>
      </div>
    );
  }
  const yMin = Math.floor(Math.min(...ys) / 10) * 10;
  const yMax = Math.ceil(Math.max(...ys) / 10) * 10;
  const yRange = Math.max(10, yMax - yMin);
  const xStep = xs.length > 1 ? innerW / (xs.length - 1) : innerW / 2;
  const points = buckets
    .map((b, i) => {
      if (b.avg_composite == null) return null;
      const x = padding.left + i * xStep;
      const y = padding.top + innerH - ((b.avg_composite - yMin) / yRange) * innerH;
      return { x, y, b };
    })
    .filter(Boolean);
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x.toFixed(1)} ${padding.top + innerH} L ${points[0].x.toFixed(1)} ${padding.top + innerH} Z`;

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-[#e6f1ff]">Portfolio Trend</h3>
          <p className="text-xs text-[#a8b2d1]">Weekly portfolio posture composite. Lower is healthier.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-[#8892b0]">Latest</div>
          {(() => {
            const latest = buckets[buckets.length - 1]?.avg_composite;
            if (latest == null) {
              return <div className="text-xl font-bold text-[#8892b0]">—</div>;
            }
            const style = getRiskBandStyle(latest);
            return (
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold"
                style={{ background: style.bg, color: style.text, border: `1px solid ${style.border}` }}
              >
                <span aria-hidden="true">{style.icon}</span>
                <span>{style.label}</span>
              </span>
            );
          })()}
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="text-cyan-300">
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
          const y = padding.top + innerH * (1 - frac);
          const label = Math.round(yMin + yRange * frac);
          return (
            <g key={i}>
              <line x1={padding.left} x2={padding.left + innerW} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <text x={padding.left - 6} y={y + 4} fontSize="10" textAnchor="end" fill="#64748b">{label}</text>
            </g>
          );
        })}
        {/* Area */}
        <path d={areaD} fill="rgba(6, 182, 212, 0.15)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="rgb(34, 211, 238)" strokeWidth="2" />
        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill="rgb(34, 211, 238)" />
            <title>{`${p.b.week_start} · composite ${p.b.avg_composite} · ${p.b.orgs_covered} org(s), ${p.b.sample_count} snapshot(s)`}</title>
          </g>
        ))}
        {/* X labels (first, middle, last) */}
        {[0, Math.floor(buckets.length / 2), buckets.length - 1].filter((i, idx, arr) => arr.indexOf(i) === idx).map((i) => {
          const x = padding.left + i * xStep;
          return (
            <text key={i} x={x} y={height - 8} fontSize="10" textAnchor="middle" fill="#64748b">
              {new Date(buckets[i].week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

const BoardReportConcentration = ({ companies, heatmap }) => {
  if (!companies || companies.length === 0) return null;
  // Reuse the same concentration logic condensed for the report
  const byIndustry = new Map();
  for (const co of companies) {
    const industry = (co.industry || co.latest_assessment?.industry_vertical || 'Unknown').trim() || 'Unknown';
    if (!byIndustry.has(industry)) byIndustry.set(industry, 0);
    byIndustry.set(industry, byIndustry.get(industry) + 1);
  }
  const total = companies.length;
  const opByIndustry = new Map((heatmap?.industries || []).map((r) => [r.industry, r.overall_score]));
  const rows = Array.from(byIndustry.entries())
    .map(([industry, count]) => ({
      industry,
      count,
      pct: Math.round((count / total) * 1000) / 10,
      op: opByIndustry.get(industry) ?? null
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
      <h3 className="text-base font-semibold text-[#e6f1ff] mb-3">Top Concentrations</h3>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.industry} className="flex items-center gap-3 text-sm">
            <div className="w-40 text-[#e6f1ff] truncate">{r.industry}</div>
            <div className="flex-1 bg-[rgba(255,255,255,0.04)] rounded h-4 overflow-hidden">
              <div className={`h-full ${r.pct >= 25 ? 'bg-orange-400' : r.pct >= 15 ? 'bg-amber-400' : 'bg-cyan-400'}`} style={{ width: `${Math.min(100, r.pct * 2)}%` }} />
            </div>
            <div className="w-24 text-right text-[#a8b2d1]">
              {r.count} <span className="text-[#64748b]">({r.pct}%)</span>
            </div>
            <div className="w-12 text-right">
              {r.op != null ? <span className={`text-xs px-1.5 py-0.5 rounded ${r.op >= 75 ? 'bg-red-500/20 text-red-200' : r.op >= 60 ? 'bg-orange-500/20 text-orange-200' : r.op >= 45 ? 'bg-amber-500/20 text-amber-200' : 'bg-emerald-500/20 text-emerald-200'}`}>OP {r.op}</span> : <span className="text-[#64748b] text-xs">—</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FA-11-c — Pool Diversification (Insurance underwriter variant)
// Industry × risk-band concentration matrix plus threshold alerts. Answers the
// underwriter's question: "Where is my book clustered, and is any single
// industry/risk combo big enough to wipe out the pool on one event?"
// All compute is client-side against the companies array the dashboard
// already loads — no new backend call.
// ─────────────────────────────────────────────────────────────────────────────
const RISK_BANDS = [
  { key: 'low',      label: 'Low',      min: 0,  max: 29,  color: 'bg-emerald-500/25 text-emerald-200', threshold: 'rgba(16, 185, 129, 0.35)' },
  { key: 'medium',   label: 'Medium',   min: 30, max: 49,  color: 'bg-cyan-500/25    text-cyan-200',    threshold: 'rgba(6, 182, 212, 0.35)'  },
  { key: 'high',     label: 'High',     min: 50, max: 69,  color: 'bg-amber-500/25   text-amber-200',   threshold: 'rgba(245, 158, 11, 0.35)' },
  { key: 'critical', label: 'Critical', min: 70, max: 100, color: 'bg-red-500/25     text-red-200',     threshold: 'rgba(239, 68, 68, 0.35)'  }
];

const bandForScore = (score) => {
  if (score == null) return null;
  if (score >= 70) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
};

const ConcentrationRiskPanel = ({ companies = [], posture = [], heatmap }) => {
  if (!companies || companies.length === 0) return null;

  const total = companies.length;

  // Build industry × risk-band distribution
  const byIndustry = new Map();
  for (const co of companies) {
    const industry = (co.industry || co.latest_assessment?.industry_vertical || 'Unknown').trim() || 'Unknown';
    const riskScore = Number(co.risk_score ?? co.latest_assessment?.risk_score);
    const band = bandForScore(riskScore);
    if (!byIndustry.has(industry)) {
      byIndustry.set(industry, { industry, count: 0, bands: { low: 0, medium: 0, high: 0, critical: 0, unknown: 0 } });
    }
    const row = byIndustry.get(industry);
    row.count++;
    row.bands[band || 'unknown']++;
  }

  // Cross-reference OP overall pressure + posture (journey / SPRS) per industry
  const opByIndustry = new Map(
    (heatmap?.industries || []).map((r) => [r.industry, r.overall_score])
  );
  const postureByCompany = new Map((posture || []).map((p) => [p.foundations_company_id, p]));
  for (const co of companies) {
    const industry = (co.industry || co.latest_assessment?.industry_vertical || 'Unknown').trim() || 'Unknown';
    const row = byIndustry.get(industry);
    const p = postureByCompany.get(co.company_id);
    if (p?.ato_step || (p?.sprs_score != null)) {
      row.journey_clients = (row.journey_clients || 0) + 1;
    }
    if (p?.sprs_score != null && p.sprs_score < 50) {
      row.weak_sprs = (row.weak_sprs || 0) + 1;
    }
  }

  const rows = Array.from(byIndustry.values()).map((r) => ({
    ...r,
    pct_of_book: Math.round((r.count / total) * 1000) / 10,
    op_score: opByIndustry.get(r.industry) ?? null,
    journey_clients: r.journey_clients || 0,
    weak_sprs: r.weak_sprs || 0
  })).sort((a, b) => b.count - a.count);

  // Threshold alerts
  const alerts = [];
  const CONCENTRATION_PCT = 20;
  for (const r of rows) {
    if (r.pct_of_book >= CONCENTRATION_PCT) {
      alerts.push({
        severity: r.pct_of_book >= 35 ? 'critical' : r.pct_of_book >= 25 ? 'high' : 'moderate',
        message: `${r.pct_of_book}% of your book is in ${r.industry} (${r.count} clients)`
      });
    }
    const highAndCritical = (r.bands.high || 0) + (r.bands.critical || 0);
    if (highAndCritical >= 3 && highAndCritical / r.count >= 0.5) {
      alerts.push({
        severity: 'high',
        message: `${r.industry}: ${highAndCritical} of ${r.count} clients at High/Critical risk`
      });
    }
    if (r.op_score != null && r.op_score >= 70 && r.count >= 2) {
      alerts.push({
        severity: 'moderate',
        message: `${r.industry} has ${r.count} clients and is under elevated external pressure (OP ${r.op_score})`
      });
    }
  }

  const alertColor = (sev) => ({
    critical: 'bg-red-500/15 border-red-500/40 text-red-200',
    high:     'bg-orange-500/15 border-orange-500/40 text-orange-200',
    moderate: 'bg-amber-500/15 border-amber-500/40 text-amber-200'
  })[sev];

  // Cell shading scales with count (within industry row)
  const cellShade = (count, rowTotal, band) => {
    if (count === 0) return { background: 'transparent', color: '#64748b' };
    const share = count / rowTotal;
    const bandDef = RISK_BANDS.find((b) => b.key === band);
    // Scale alpha by share (0.15 → 0.55)
    const alpha = 0.15 + Math.min(0.40, share * 0.40);
    const rgb = bandDef.threshold.match(/\d+, \d+, \d+/)[0];
    return { background: `rgba(${rgb}, ${alpha})`, color: '#e6f1ff' };
  };

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h4 className="text-sm font-semibold text-[#e6f1ff]">Concentration Risk</h4>
          <p className="text-xs text-[#a8b2d1]">
            Industry × risk-band distribution across your book of {total} client{total === 1 ? '' : 's'}.
            Threshold alerts fire above {CONCENTRATION_PCT}% single-industry concentration.
          </p>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="space-y-1.5 mb-4">
          {alerts.map((a, i) => (
            <div key={i} className={`text-xs px-3 py-2 rounded-md border ${alertColor(a.severity)}`}>
              <span className="font-semibold uppercase tracking-wide opacity-80 mr-2">{a.severity}</span>
              {a.message}
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-separate" style={{ borderSpacing: '2px' }}>
          <thead>
            <tr>
              <th className="text-left py-2 pr-3 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium">Industry</th>
              <th className="text-center py-2 px-2 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium w-14">N</th>
              <th className="text-center py-2 px-2 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium w-16">% book</th>
              {RISK_BANDS.map((b) => (
                <th key={b.key} className="text-center py-2 px-1 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium" title={`Risk score ${b.min}-${b.max}`}>
                  {b.label}
                </th>
              ))}
              <th className="text-center py-2 px-2 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium w-16">OP</th>
              <th className="text-center py-2 px-2 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium w-20">On journey</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.industry}>
                <td className="py-2 pr-3 text-[#e6f1ff] font-medium">{r.industry}</td>
                <td className="py-1 px-1 text-center">
                  <span className="text-[#e6f1ff] font-semibold">{r.count}</span>
                </td>
                <td className="py-1 px-1 text-center">
                  <span className={r.pct_of_book >= CONCENTRATION_PCT ? 'text-orange-300 font-semibold' : 'text-[#a8b2d1]'}>
                    {r.pct_of_book}%
                  </span>
                </td>
                {RISK_BANDS.map((b) => {
                  const count = r.bands[b.key] || 0;
                  return (
                    <td key={b.key} className="py-1 px-1 text-center">
                      <div
                        className="rounded py-1 px-1 font-semibold text-xs"
                        style={cellShade(count, r.count, b.key)}
                        title={`${count} ${b.label}-risk clients in ${r.industry}`}
                      >
                        {count || ''}
                      </div>
                    </td>
                  );
                })}
                <td className="py-1 px-1 text-center">
                  {r.op_score != null ? (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded font-semibold ${
                        r.op_score >= 75 ? 'bg-red-500/20 text-red-200' :
                        r.op_score >= 60 ? 'bg-orange-500/20 text-orange-200' :
                        r.op_score >= 45 ? 'bg-amber-500/20 text-amber-200' :
                                           'bg-emerald-500/20 text-emerald-200'
                      }`}
                      title="Outside Pressures overall score"
                    >
                      {r.op_score}
                    </span>
                  ) : <span className="text-[#64748b]">—</span>}
                </td>
                <td className="py-1 px-1 text-center">
                  <span className={r.journey_clients > 0 ? 'text-cyan-300 font-semibold' : 'text-[#64748b]'}>
                    {r.journey_clients || '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-[11px] text-[#8892b0]">
        Darker cells = more clients in that industry × risk-band. OP column is the industry's Outside Pressures overall score from the heatmap above. "On journey" counts clients actively on a SPRS or ATO journey.
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tile 12 — Outside Pressures portfolio heatmap
// Industry × Outside-Pressures-category heatmap. Rows sorted by overall risk.
// Uses Foundations 360-family category scoring (base threat intel) aggregated
// at the industry level — same module that drives the per-session Outside
// Pressures view on the assessment results page.
// ─────────────────────────────────────────────────────────────────────────────
const OP_CATEGORIES_ORDER = [
  'Regulatory & Compliance',
  'Evolving Threat Landscape',
  'Geopolitical Risks',
  'Supply Chain Vulnerabilities',
  'Market & Economic Conditions',
  'Public Perception & Reputation',
  'Technological Advancements'
];

const OP_CATEGORY_SHORT = {
  'Regulatory & Compliance':        'Reg.',
  'Evolving Threat Landscape':      'Threats',
  'Geopolitical Risks':             'Geo',
  'Supply Chain Vulnerabilities':   'Supply',
  'Market & Economic Conditions':   'Market',
  'Public Perception & Reputation': 'Public',
  'Technological Advancements':     'Tech'
};

const OutsidePressuresHeatmap = ({ heatmap, loading }) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
        <div className="text-sm text-[#a8b2d1] animate-pulse">Loading portfolio risk landscape…</div>
      </div>
    );
  }
  if (!heatmap || !Array.isArray(heatmap.industries) || heatmap.industries.length === 0) {
    return null;
  }

  // Color a cell by score intensity (higher = more risk = warmer)
  const cellStyle = (score) => {
    if (score == null) return { background: 'rgba(255,255,255,0.03)', color: '#64748b' };
    const normalized = Math.max(0, Math.min(100, score));
    if (normalized >= 75) return { background: 'rgba(239, 68, 68, 0.35)', color: '#fecaca' };  // red
    if (normalized >= 60) return { background: 'rgba(249, 115, 22, 0.30)', color: '#fed7aa' }; // orange
    if (normalized >= 45) return { background: 'rgba(245, 158, 11, 0.25)', color: '#fde68a' }; // amber
    if (normalized >= 30) return { background: 'rgba(6, 182, 212, 0.25)',  color: '#a5f3fc' };  // cyan
    return                        { background: 'rgba(16, 185, 129, 0.25)', color: '#a7f3d0' };  // emerald
  };

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.03)] p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-[#e6f1ff]">Portfolio Risk Landscape</h4>
          <p className="text-xs text-[#a8b2d1]">
            Outside Pressures category scores by industry · {heatmap.industry_count} industr{heatmap.industry_count === 1 ? 'y' : 'ies'}
            {' · '}
            {heatmap.total_clients} client{heatmap.total_clients === 1 ? '' : 's'}
          </p>
          {heatmap.pending_count > 0 && (
            <p className="text-xs text-[#a8b2d1] mt-1">
              {heatmap.pending_count} industr{heatmap.pending_count === 1 ? 'y is' : 'ies are'} still being built and will fill in shortly.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#8892b0]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: 'rgba(16, 185, 129, 0.4)' }} /> Lower</span>
          <span>→</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: 'rgba(239, 68, 68, 0.5)' }} /> Higher risk</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-separate" style={{ borderSpacing: '2px' }}>
          <thead>
            <tr>
              <th className="text-left py-2 pr-3 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium">Industry</th>
              <th className="text-center py-2 px-2 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium w-16">Overall</th>
              {OP_CATEGORIES_ORDER.map((cat) => (
                <th key={cat} className="text-center py-2 px-1 text-[10px] tracking-wide text-[#8892b0] font-medium" title={cat}>
                  {OP_CATEGORY_SHORT[cat]}
                </th>
              ))}
              <th className="text-center py-2 pl-2 text-[11px] uppercase tracking-wide text-[#8892b0] font-medium w-16">Clients</th>
            </tr>
          </thead>
          <tbody>
            {heatmap.industries.map((row) => {
              const catByName = new Map((row.categories || []).map((c) => [c.name, c]));
              return (
                <tr key={row.industry}>
                  <td className="py-2 pr-3 text-[#e6f1ff] font-medium">{row.industry}</td>
                  <td className="py-1 px-1 text-center">
                    <div className="rounded py-1 px-2 font-semibold" style={cellStyle(row.overall_score)}>
                      {row.pending ? <span className="text-[10px] font-normal animate-pulse">Building…</span> : (row.overall_score ?? '—')}
                    </div>
                  </td>
                  {OP_CATEGORIES_ORDER.map((cat) => {
                    const c = catByName.get(cat);
                    const score = c?.score ?? null;
                    return (
                      <td key={cat} className="py-1 px-1 text-center">
                        <div
                          className="rounded py-1 px-1 font-semibold text-xs"
                          style={cellStyle(score)}
                          title={`${row.industry} · ${cat}${score != null ? ` · ${score}` : ''}`}
                        >
                          {score != null ? score : '—'}
                        </div>
                      </td>
                    );
                  })}
                  <td className="py-1 pl-2 text-center text-[#a8b2d1]">{row.client_count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-[11px] text-[#8892b0]">
        Hover any cell for the full category name and exact score. Colors indicate relative pressure, not an absolute rating.
      </div>
    </div>
  );
};

export default PortfolioDashboard; 
